import { zodResolver } from '@hookform/resolvers/zod';
import {
  type CreateLeadRequest,
  CreateLeadRequestSchema,
  type CustomerListItem,
  type LeadDetail,
  type LeadPriority,
  scoreLeadIntent,
  type UpdateLeadRequest,
  UpdateLeadRequestSchema,
} from '@utazzo/shared';
import {
  UzBadge,
  UzButton,
  UzCard,
  UzCombobox,
  UzDialog,
  UzInput,
  UzModernDatePicker,
  UzNumberInput,
  UzSelect,
  UzSpinner,
  UzTextarea,
} from '@utazzo/ui';
import { ArrowLeft, AlertTriangle, Check, MapPin as MapPinIcon } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { CustomerPicker } from '../components/CustomerPicker';
import { DepartureCityPicker } from '../components/DepartureCityPicker';
// `DestinationCityPicker` was removed 2026-06-24 - the lead form now
// uses two destination-master dropdowns instead of a cities picker.
// The component file is still on disk in case a future ADR brings
// city-level destinations back.
import { canReviewLeadApprovalRequest } from '../lead-approval-access';
import { resolveStageHex, resolveStageTone, stageBadgeTone } from '../stage-color';
import {
  useAgentOptionsForLeads,
  useDestinationOptionsForLeads,
  useMealPlanOptions,
  useQueryMediumOptions,
  useQuerySourceOptions,
  useQueryStatusOptionsWithBucket,
  useQuerySubStatusOptionsForParent,
  useQueryTypeOptions,
} from '../use-lead-options';
import {
  useCreateLead,
  useCreateStatusChangeRequest,
  useLead,
  useTransitionLeadStage,
  useUpdateLead,
} from '../use-leads';

import { PageShell } from '@/components/page-shell';
import { useCurrentUser } from '@/features/auth/use-auth';
import { useCountriesList } from '@/features/countries/use-countries';
import { useUpdateCustomer } from '@/features/customers/use-customers';
import { useListReturn } from '@/hooks/use-list-return';

const PRIORITY_OPTIONS: ReadonlyArray<{ value: LeadPriority; label: string }> = [
  { value: 'LOW', label: 'Low' },
  { value: 'NORMAL', label: 'Normal' },
  { value: 'HIGH', label: 'High' },
  { value: 'URGENT', label: 'Urgent' },
];

const LEAD_CATEGORY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'DOMESTIC', label: 'Domestic' },
  { value: 'INTERNATIONAL', label: 'International' },
];

// ----- ADR-0035 legacy parity option lists -----
const HOTEL_CATEGORY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '1', label: '1 Star' },
  { value: '2', label: '2 Star' },
  { value: '3', label: '3 Star' },
  { value: '4', label: '4 Star' },
  { value: '5', label: '5 Star' },
];

const BOOKING_TIMELINE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'WITHIN_7_DAYS', label: '0 - 7 Days' },
  { value: 'EIGHT_TO_15_DAYS', label: '08 - 15 Days' },
  { value: 'SIXTEEN_TO_30_DAYS', label: '16 - 30 Days' },
  { value: 'ONE_TO_THREE_MONTHS', label: '1 - 3 Months' },
  { value: 'MORE_THAN_THREE_MONTHS', label: '3+ Months' },
];

// Three-state boolean select - Yes / No / Unknown (the Zod schema treats
// NULL as "operator hasn't been asked", distinct from "no").
const THREE_STATE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const YES_NO_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

// `REVERT_OPTIONS` was dropped 2026-06-25 with the "Is reverted to
// customer?" picker. Revert Pending is now derived automatically
// (Active + Proposal + !isItinerarySent) or set by the
// Move-to-Active followup flow.

/// Override the base Input's `disabled:opacity-50` with a softer fade.
/// The value still reads clearly (zinc-700, opacity 80) while the
/// muted background + border keep the "non-interactive" cue.
const DISABLED_INPUT_CLS =
  'disabled:opacity-80 disabled:bg-zinc-50 disabled:text-zinc-700 disabled:border-zinc-300';

/// Split a server-side displayName ("Biju Cr") back into first/last for
/// the read-only edit-mode customer snapshot. Single-word names show as
/// first only - mirrors the DetailPage helper of the same name.
function parseDisplayName(name: string): { first: string | null; last: string | null } {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return { first: null, last: null };
  if (parts.length === 1) return { first: parts[0] ?? null, last: null };
  return { first: parts[0] ?? null, last: parts.slice(1).join(' ') };
}

function getLeadIntakeEmail(lead: LeadDetail): string {
  return (lead.customerEmail ?? lead.customer.email ?? '').trim();
}

/// True when the customer primary was promoted from the alternate while
/// the lead intake snapshot stayed on the former primary.
function isPrimaryEmailPromoted(lead: LeadDetail): boolean {
  const intake = getLeadIntakeEmail(lead);
  const primary = (lead.customer.email ?? '').trim();
  const alternate = (lead.customer.alternateEmail ?? '').trim();
  if (!intake || !primary || !alternate) return false;
  return (
    primary.localeCompare(intake, undefined, { sensitivity: 'accent' }) !== 0 &&
    alternate.localeCompare(intake, undefined, { sensitivity: 'accent' }) === 0
  );
}

/// Alternate-email field display. After a promote swap the DB alternate
/// holds the former primary (intake snapshot) - but the form keeps
/// showing the promoted address in this input so the operator's view
/// matches what they typed.
function deriveAlternateEmailDisplay(lead: LeadDetail): string {
  const primary = (lead.customer.email ?? '').trim();
  if (isPrimaryEmailPromoted(lead)) return primary;
  return (lead.customer.alternateEmail ?? '').trim();
}

/// Derive whether "Mark as primary email" should start checked on edit.
function deriveMarkEmailAsPrimary(lead: LeadDetail): boolean {
  if (isPrimaryEmailPromoted(lead)) return true;
  const intakeEmail = getLeadIntakeEmail(lead);
  const alternateEmail = (lead.customer.alternateEmail ?? '').trim();
  if (!alternateEmail) return false;
  return alternateEmail.localeCompare(intakeEmail, undefined, { sensitivity: 'accent' }) !== 0;
}

/// Local-time YYYY-MM-DD for the "queryDate defaults to today" rule.
/// Built fresh on every form mount so a tab left open overnight still
/// captures the correct date when the operator finally hits Save.
const todayIso = (): string => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/// Derive a human-readable placeholder title for a new lead. The
/// dedicated "Lead title" input was retired from the UI - the server
/// still requires a non-empty title (`min(2)`), so we synthesize one
/// from whatever the operator already filled in. Reads, in priority
/// order: destination > category. Customer name (when present) is
/// always prefixed. "New lead" is the last-resort fallback.
function buildAutoTitle(parts: {
  customerDisplay: string;
  destinationLabel: string | null;
  leadCategory: 'DOMESTIC' | 'INTERNATIONAL' | null;
}): string {
  const customer = parts.customerDisplay.trim();
  const suffix =
    parts.destinationLabel?.trim() ||
    (parts.leadCategory === 'DOMESTIC'
      ? 'Domestic enquiry'
      : parts.leadCategory === 'INTERNATIONAL'
        ? 'International enquiry'
        : 'Enquiry');
  const composed = customer ? `${customer} - ${suffix}` : suffix;
  return composed.length >= 2 ? composed : 'New lead';
}

/// react-hook-form's onError hook. Scrolls the first invalid field
/// into view, focuses it, AND surfaces a toast - any one of which
/// catches a silent submit failure when the offending field happens
/// to be hidden (auto-derived title, etc.). Works for native inputs
/// (`name=...`) and for the custom Controlled / NumberControlled /
/// DateControlled wrappers which all render a child element with a
/// matching `[name=...]` attribute.
function scrollToFirstError(errors: Record<string, unknown>): void {
  const firstName = pickFirstErrorName(errors);
  const firstMessage = pickFirstErrorMessage(errors);
  // 2026-06-27. Always include the field NAME in the toast so the
  // operator can find the offending control immediately. The earlier
  // toast just said "Please fix: Invalid UUID" which left the
  // operator scrolling to find which dropdown they missed.
  const friendly = firstName ? humaniseFieldName(firstName) : null;
  toast.error(
    friendly && firstMessage
      ? `Please fix "${friendly}": ${firstMessage}`
      : firstMessage
        ? `Please fix: ${firstMessage}`
        : 'Please fix the highlighted fields and try again.',
  );
  if (!firstName) return;
  // Defer to next tick so RHF has had a chance to attach the
  // aria-invalid markers / error text the screen reader picks up.
  setTimeout(() => {
    const el = document.querySelector<HTMLElement>(`[name="${cssEscape(firstName)}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (typeof (el as HTMLInputElement).focus === 'function') {
      (el as HTMLInputElement).focus({ preventScroll: true });
    }
  }, 0);
}

function pickFirstErrorMessage(errors: Record<string, unknown>): string | null {
  for (const key of Object.keys(errors)) {
    const value = errors[key] as Record<string, unknown> | undefined;
    if (!value) continue;
    if (typeof value === 'object' && 'message' in value && typeof value.message === 'string') {
      return value.message;
    }
    if (typeof value === 'object') {
      const nested = pickFirstErrorMessage(value as Record<string, unknown>);
      if (nested) return nested;
    }
  }
  return null;
}

function pickFirstErrorName(errors: Record<string, unknown>, prefix = ''): string | null {
  for (const key of Object.keys(errors)) {
    const value = errors[key] as Record<string, unknown> | undefined;
    if (!value) continue;
    if (typeof value === 'object' && 'ref' in value) {
      return prefix ? `${prefix}.${key}` : key;
    }
    if (typeof value === 'object') {
      const nested = pickFirstErrorName(
        value as Record<string, unknown>,
        prefix ? `${prefix}.${key}` : key,
      );
      if (nested) return nested;
    }
  }
  return null;
}

function cssEscape(value: string): string {
  return value.replace(/(["\\])/g, '\\$1');
}

/// 2026-06-27. Map an RHF field path ("destinationCityId",
/// "customer.firstName", "products.0.destination") to a human-readable
/// label for the error toast. Known fields use a curated label;
/// unknown ones get a CamelCase->Title-Case fallback.
const FRIENDLY_FIELD_LABELS: Record<string, string> = {
  queryTypeId: 'Query type',
  querySourceId: 'Query source',
  querySubSourceId: 'Query sub-source',
  queryMediumId: 'Query medium',
  leadCategory: 'Lead category (Domestic / International)',
  departureCityId: 'Departure city',
  destinationCityId: 'Destination',
  destinationId: 'Marketing destination',
  branchId: 'Branch',
  departmentId: 'Department',
  assignedAgentId: 'Assigned agent',
  customerId: 'Customer',
  budgetAmount: 'Budget',
  priority: 'Lead Intent',
  budgetCurrency: 'Budget currency',
  expectedTravelStart: 'Travel start',
  expectedTravelEnd: 'Travel end',
  travellerAdults: 'Adults',
  travellerChildren: 'Children',
  travellerInfants: 'Infants',
  childAges: 'Child ages',
  title: 'Lead title',
  initialStatusId: 'Status',
  initialSubStatusId: 'Sub-status',
  preferredHotelCategory: 'Preferred hotel category',
  bookingTimeline: 'Booking timeline',
  isFlightBooked: 'Flight booked',
  preferredTravelMode: 'Preferred travel mode',
  ttvAmount: 'TTV',
  ttvCurrency: 'TTV currency',
  'customer.firstName': 'Customer first name',
  'customer.lastName': 'Customer last name',
  'customer.email': 'Customer email',
  'customer.mobileNumber': 'Customer mobile number',
  'customer.altPhone': 'Customer alternate phone',
  'customer.alternateEmail': 'Customer alternate email',
};

/**
 * 2026-07-19. Customer email is mandatory when creating a lead with a NEW
 * inline customer.
 *
 * Enforced here rather than on `NewCustomerInlineSchema` in @utazzo/shared
 * on purpose: that schema also backs `CreateLeadRequestSchema`, which the
 * API DTO (`CreateLeadRequestDto`) and the CSV lead importer
 * (`imports/importers/leads.importer.ts`) both consume. Tightening it there
 * would start rejecting imported rows that legitimately have only a phone
 * number. Scoping the rule to the admin create form leaves every other
 * intake path exactly as it was.
 *
 * Only fires when `customer` is present - linking an EXISTING customer sends
 * `customerId` with no inline block, and that record's contact details are
 * already on file (the form disables these inputs in that mode).
 */
const CreateLeadFormSchema = CreateLeadRequestSchema.superRefine((value, ctx) => {
  // Lead Intent is mandatory when manually creating a lead (like budget /
  // category). The API schema keeps `priority` optional so the importer can
  // leave weak leads blank; the create FORM requires an explicit pick.
  if (value.priority === null || value.priority === undefined) {
    ctx.addIssue({ code: 'custom', path: ['priority'], message: 'Lead Intent is required' });
  }
  if (!value.customer) return;
  const email = value.customer.email?.trim() ?? '';
  if (email.length === 0) {
    ctx.addIssue({
      code: 'custom',
      path: ['customer', 'email'],
      message: 'Email is required',
    });
  }
});

/// 2026-06-27. The list of CreateLeadRequest fields that are zod
/// UUIDs. A `Controlled` (UzSelect) the operator never picked
/// leaves an empty string in form state; running that through
/// `z.uuid()` throws "Invalid UUID" (confusing). We pre-promote ""
/// to undefined for these fields so the schema's required vs
/// optional rules fire their natural messages instead.
const LEAD_UUID_FIELDS: ReadonlyArray<string> = [
  'queryTypeId',
  'querySourceId',
  'querySubSourceId',
  'queryMediumId',
  'initialStatusId',
  'initialSubStatusId',
  'assignedAgentId',
  'createdById',
  'branchId',
  'departureCityId',
  'destinationCityId',
  'destinationId',
  'customerId',
];

function sanitiseUuidLikeFields<T extends Record<string, unknown>>(values: T): T {
  const next: Record<string, unknown> = { ...values };
  for (const field of LEAD_UUID_FIELDS) {
    const v = next[field];
    if (typeof v === 'string' && v.trim() === '') {
      next[field] = undefined;
    }
  }
  return next as T;
}

function humaniseFieldName(path: string): string {
  if (FRIENDLY_FIELD_LABELS[path]) return FRIENDLY_FIELD_LABELS[path];
  // Strip array indices ("products.0.foo" -> "products.foo") then
  // CamelCase + dotted-path to spaces + Title Case the leading word.
  const cleaned = path
    .split('.')
    .filter((seg) => !/^\d+$/.test(seg))
    .map((seg) => seg.replace(/([A-Z])/g, ' $1').toLowerCase())
    .join(' ');
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

// Loose `Partial<>` typing - `leadCategory` is required at *submit*
// time (enforced by zod) but shouldn't have a meaningful default (the
// operator must choose). RHF accepts partial `defaultValues`; the
// placeholder shows until they pick.
// `departureCityId` is optional on create (required only when moving
// to Active). `destinationCityId` is no longer required (2026-06-10) -
// the lead form drives destination through the Marketing Destination picker.
const buildCreateDefaults = (): Partial<CreateLeadRequest> => ({
  title: '',
  // customerId omitted - the form supplies inline `customer` fields by default
  // (auto-create flow). Pickering an existing customer fills `customerId`
  // instead and clears `customer`.
  // 2026-06-27. Query enquiry fields (type / source / medium / URL) are
  // optional at intake - the server defaults query type when omitted.
  queryTypeId: undefined as unknown as string,
  // No default - Lead Intent is mandatory, the operator must pick (like
  // leadCategory). Placeholder shows until they choose.
  priority: undefined,
  travellerAdults: 1,
  travellerChildren: 0,
  travellerInfants: 0,
  childAges: [],
  // Query date defaults to "today" and is locked in the UI - it represents
  // when the enquiry came in, captured at form mount.
  queryDate: todayIso(),
  isFirstTimeTraveler: true,
});

/**
 * Full-screen create / edit lead form.
 *
 * Two sections (per `u-desgin/feature-leads.jsx` `LeadForm`):
 *
 *   1. **Customer** - picker that searches the customer master. Auto-create-
 *      from-form is out of scope until `CustomersService.upsertByEmailOrPhone`
 *      lands (migration plan Phase A0); for now operators search-or-create
 *      via /customers/new.
 *   2. **Enquiry & travel** - title, query type/source/medium, priority,
 *      assigned agent / branch, budget, travel dates, pax counts, notes.
 *
 * Status is server-defaulted on create (the seeded "new" QueryStatus) - the
 * stage-transition flow on the detail page is the canonical place to change
 * stage after creation.
 */
/**
 * Optional embedding props (2026-06-25). When the form is rendered inside
 * a modal (e.g. the LeadStatusChanger's "complete the lead" dialog), the
 * caller passes the lead id directly + a callback to handle save success
 * without navigating away from the underlying lead detail page.
 */
interface LeadFormPageProps {
  /** When set, used in place of `useParams().id`. The form is treated as
   * an edit-mode form regardless of the URL. */
  leadIdOverride?: string;
  /** When set, called after a successful save instead of `navigate()`.
   * The host can close the modal + retry whatever it was about to do.
   * Also receives the freshly-saved `LeadDetail` so the host can re-check
   * derived predicates (e.g. active-readiness) without waiting for a
   * cache invalidation round-trip. */
  onSaveSuccess?: (leadId: string, updatedLead?: LeadDetail) => void;
  /** When true, render without the PageShell wrapper - the host (a
   * modal, a side panel) supplies its own chrome. */
  embedded?: boolean;
  /**
   * 2026-07-09. When true, render only the sections that participate
   * in the active-readiness gate (Enquiry + Customer + Travel-core).
   * The "Additional travel details" block (TTV / preferences / meal
   * plan / expected payment date / requirement) and the "Notes" card
   * stay hidden so the operator focuses on the gaps the "Complete the
   * lead before moving to Active" modal is actually asking them to
   * fill. Used by `LeadMissingFieldsModal`.
   */
  focusOnActiveReadiness?: boolean;
  /**
   * 2026-08-10. When true AND focusOnActiveReadiness is true, ALSO render the
   * "Additional travel details" section that hosts the six travel-intent
   * fields (bookingTimeline, isFirstTimeTraveler, isHoneymoonCouple,
   * hasValidPassport, isFlightBooked, isDecisionMaker). Used by the
   * travel-intent variants of the missing-fields modal so the operator can
   * fill those without leaving the focused form. Ignored when
   * `focusOnActiveReadiness` is false (the section is already visible).
   */
  focusIncludeTravelIntent?: boolean;
  /**
   * 2026-07-09. Override the initial values of the inner Query status /
   * Sub status pickers (usually hydrated from `lead.currentStatus`).
   * When the modal opens because the operator picked "Active" from the
   * outer header picker, we want the inner picker to match that target
   * automatically so a Save Changes click:
   *   - runs the field PATCH,
   *   - then fires the transition to the SAME target,
   *   - and the outer pill / cache reflect the new stage in one round trip.
   * `originalStatusId` still snapshots the LEAD's current status so
   * `stageChanged` is correctly true when the override differs.
   */
  initialStatusIdOverride?: string | null;
  initialSubStatusIdOverride?: string | null;
}

export const LeadFormPage = ({
  leadIdOverride,
  onSaveSuccess,
  embedded = false,
  focusOnActiveReadiness = false,
  focusIncludeTravelIntent = false,
  initialStatusIdOverride,
  initialSubStatusIdOverride,
}: LeadFormPageProps = {}): React.JSX.Element => {
  const { id: routeId } = useParams<{ id: string }>();
  const id = leadIdOverride ?? routeId;
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const backToList = useListReturn('/leads');
  const me = useCurrentUser();

  // Role-based field visibility / disabled rules. Admin can edit
  // intake-classification fields (query type / source / medium) at any
  // point; other roles only see them locked on edit and not at all on
  // create. SALES_LEAD can pick / reassign the assignee (hierarchy-
  // scoped) on create and on OPEN-bucket edit.
  const isAdmin = me?.role === 'ADMIN';
  const isSalesLead = me?.role === 'SALES_LEAD';
  const isSalesAgent = me?.role === 'SALES_AGENT';
  // 2026-08-06. SALES_LEAD approval-parity: the backend now blocks BOTH
  // SALES_AGENT and SALES_LEAD from a direct destination edit (each must
  // file a DESTINATION_CHANGE request), and locks a closed (WON/LOST)
  // lead's edit form to admin-only. This combined flag drives those two
  // gates so a SALES_LEAD gets the same request-routing / read-only
  // behavior as a SALES_AGENT instead of a 403 dead-end.
  const isAgentOrLead = isSalesAgent || isSalesLead;
  const canPickAssignee = isAdmin || isSalesLead;
  const canReassignOwnerOnEdit = canPickAssignee;

  const leadQuery = useLead(isEdit ? id : null);
  const isOpenLead = (leadQuery.data?.currentBucket ?? null) === 'OPEN';
  // 2026-07-09. Whether the loaded lead is currently sitting on the
  // Active / Hot cell. Drives soft-required + hint on the Expected
  // payment date field below - see the DateControlled render for the
  // full rationale.
  const isActiveHot =
    leadQuery.data?.currentStatus.code === 'active' &&
    leadQuery.data?.currentSubStatus?.code === 'hot';
  const pendingApproval = leadQuery.data?.pendingStatusChangeRequest ?? null;
  const pendingDestinationApproval =
    pendingApproval?.kind === 'DESTINATION_CHANGE' ? pendingApproval : null;
  const originalDestinationCityId = leadQuery.data?.destinationCity?.id ?? null;
  const create = useCreateLead();
  const update = useUpdateLead();
  const requestApproval = useCreateStatusChangeRequest();
  // Only the lead-form's alternate-contact field round-trips to the
  // customer master; the rest of the customer pane is read-only on
  // edit. This handler PATCHes /customers/:id alongside the lead PATCH.
  const updateCustomer = useUpdateCustomer();
  // Re-hydrate edit form only when the lead record changes (initial
  // load or post-save), not on background refetches - otherwise a
  // stale cache refresh wipes checkbox state mid-edit.
  const hydratedLeadVersionRef = useRef<string | null>(null);

  useEffect(() => {
    hydratedLeadVersionRef.current = null;
  }, [id]);

  // Option hooks
  const queryTypes = useQueryTypeOptions();
  const querySources = useQuerySourceOptions();
  const queryMediums = useQueryMediumOptions();
  // `useBranchOptions()` was dropped 2026-06-24 with the Branch
  // picker - branchId is now auto-seeded from the assigned agent.
  const agents = useAgentOptionsForLeads();
  // Edit mode: hierarchy-scoped options from assignable-agents, plus the
  // lead's current owner when they're outside the caller's team (display
  // only - SALES_LEAD can only pick self + direct reports).
  const assigneePickerAgents = useMemo(() => {
    if (!isEdit) return agents;
    const currentId = leadQuery.data?.assignedAgent?.id;
    const currentName = leadQuery.data?.assignedAgent?.fullName;
    if (!currentId || agents.options.some((o) => o.value === currentId)) return agents;
    return {
      ...agents,
      options: [{ value: currentId, label: currentName ?? 'Current owner' }, ...agents.options],
    };
  }, [isEdit, agents, leadQuery.data?.assignedAgent]);
  // Both departure and destination cities are now server-side searched
  // via the shared City picker (DepartureCityPicker / DestinationCityPicker),
  // backed by the same City master endpoint with `isActive=true +
  // countryIsActive=true`. The static `useCityOptionsForLeads` dropdown
  // was retired with that switch.
  const destinations = useDestinationOptionsForLeads();
  // Meal-plan dropdown is now master-driven (ADR-0047) - admins
  // manage the list through Masters -> Meal Plans instead of the
  // codebase carrying the enum.
  const mealPlans = useMealPlanOptions();

  // Display labels for the currently-selected departure / destination
  // cities. RHF only holds the UUIDs, so we mirror the human labels
  // here to avoid an extra fetch when the form hydrates in edit mode.
  const [departureCityLabel, setDepartureCityLabel] = useState<string | null>(null);
  // `destinationCityLabel` was removed 2026-06-24 - the destination
  // field is now a Controlled select wired to the destinations
  // master, so the label is resolved from `destinations.options` on
  // demand instead of being held in local state.

  // Edit-mode manual stage move. Backed by `POST /leads/:id/transition`
  // (preserves stage_history + outbox audit). Kept OUT of the regular
  // updateForm because the transition payload is its own DTO with its
  // own server-side validation (LOST requires lostReasonId, etc.).
  //
  // - `statusId` / `subStatusId` mirror the lead's current values
  //   hydrated from `leadQuery.data`. When the operator changes them
  //   the local state diverges; `submitUpdate` fires the transition
  //   on save iff the values differ from the original.
  // - `originalStatusId` snapshots the loaded values so we can diff
  //   without re-fetching the lead.
  const [statusId, setStatusId] = useState<string | null>(null);
  const [subStatusId, setSubStatusId] = useState<string | null>(null);
  const [originalStatusId, setOriginalStatusId] = useState<string | null>(null);
  const [originalSubStatusId, setOriginalSubStatusId] = useState<string | null>(null);
  const statusOptions = useQueryStatusOptionsWithBucket();
  const subStatusOptions = useQuerySubStatusOptionsForParent(statusId);
  const transitionStage = useTransitionLeadStage();

  // 2026-06-25: Query Status dropdown - always includes the lead's
  // current status so the selected value matches the header badge.
  // Still excludes `won` and `long_followup` as change targets.
  const filteredStatusOptions = useMemo(
    () =>
      statusOptions.options.filter((opt) => {
        const row = statusOptions.data.find((s) => s.id === opt.value);
        if (!row) return false;
        if (row.id === originalStatusId) return true;
        if (row.code === 'won') return false;
        if (row.code === 'long_followup') return false;
        return true;
      }),
    [statusOptions.options, statusOptions.data, originalStatusId],
  );

  // 2026-06-25: when the operator picks `active` for the first
  // time after a status change, default the sub-status to
  // `proposal` (mirrors LeadStatusChanger's behavior). Skips when
  // a sub-status is already picked so a manual choice isn't
  // overwritten.
  const pickedStatusRow = useMemo(
    () => statusOptions.data.find((s) => s.id === statusId) ?? null,
    [statusOptions.data, statusId],
  );
  const pickedIsActive = pickedStatusRow?.code === 'active';
  useEffect(() => {
    if (!pickedIsActive) return;
    if (subStatusId) return;
    const proposal = subStatusOptions.data.find((s) => s.code === 'proposal');
    if (proposal) setSubStatusId(proposal.id);
  }, [pickedIsActive, subStatusId, subStatusOptions.data]);

  // Two modes for "who is this lead for":
  //   - `customer = CustomerListItem`  → operator picked an existing record.
  //                                       The form posts `customerId`.
  //   - `customer = null` + `customerForm` populated → auto-create path.
  //                                       The form posts `customer: {…}` and
  //                                       the server upserts by email/phone.
  // Default is the inline (auto-create) form so the operator can type without
  // a click; the "Link existing customer" toggle reveals the search picker.
  const [customer, setCustomer] = useState<CustomerListItem | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  // 2026-07-09. Manual destination-change flow (sales agents only).
  // See the "Change destination" button + ChangeDestinationDialog below.
  const [showChangeDestinationDialog, setShowChangeDestinationDialog] = useState(false);
  const [changeDestinationId, setChangeDestinationId] = useState<string>('');
  const [changeDestinationNote, setChangeDestinationNote] = useState<string>('');
  const [customerForm, setCustomerForm] = useState({
    firstName: '',
    lastName: '',
    primaryName: '',
    email: '',
    mobileCountry: '91',
    mobileNumber: '',
    // ADR-0035 legacy parity - Alternate Contact #
    altPhone: '',
    // 2026-06-25: Alternate email + "Mark as primary email" checkbox.
    // The lead's `email` field is locked on edit (it's a snapshot
    // from intake); to update the customer's authoritative email
    // the operator types into `alternateEmail` and ticks the
    // checkbox - the save handler then PATCHes the customer.
    alternateEmail: '',
    markEmailAsPrimary: false,
    // 2026-06-25: Corporate name field. Shown only when isCorporate
    // toggle is YES; persisted to the customer's `companyName`
    // column on save.
    companyName: '',
  });
  const setCustomerField = (field: keyof typeof customerForm, value: string | boolean): void =>
    setCustomerForm((prev) => ({ ...prev, [field]: value }));

  // 2026-06-25: isCorporate is now editable in edit mode (form-only
  // state - hydrated from the loaded customer.kind, persisted via
  // the customer PATCH on save). Kept local so the existing
  // customer + lead PATCH flow doesn't have to thread it through
  // the lead schema.
  const [isCorporate, setIsCorporate] = useState<boolean>(false);

  const createForm = useForm<CreateLeadRequest>({
    // Departure city is optional on create (required only when moving to Active).
    resolver: zodResolver(CreateLeadFormSchema) as Resolver<CreateLeadRequest>,
    defaultValues: buildCreateDefaults() as Partial<CreateLeadRequest>,
  });
  const updateForm = useForm<UpdateLeadRequest>({
    resolver: zodResolver(UpdateLeadRequestSchema) as Resolver<UpdateLeadRequest>,
    defaultValues: {},
  });

  // Default "First time traveler?" to Yes on create. Linking an existing
  // customer switches it to No; clearing the link restores Yes.
  const handleCustomerChange = useCallback(
    (next: CustomerListItem | null): void => {
      setCustomer(next);
      if (next) {
        setIsCorporate(next.kind === 'CORPORATE');
        setCustomerForm((prev) => ({ ...prev, companyName: next.companyName ?? '' }));
      } else {
        setIsCorporate(false);
        setCustomerForm((prev) => ({ ...prev, companyName: '' }));
      }
      if (isEdit) return;
      createForm.setValue('isFirstTimeTraveler', next ? false : true);
    },
    [isEdit, createForm],
  );

  // Seed `createdById` to the signed-in user in create mode. Mirrors the
  // legacy "Created By" default - operators rarely override it but can
  // re-assign attribution when capturing a lead on a colleague's behalf.
  useEffect(() => {
    if (isEdit) return;
    if (!me?.id) return;
    const current = createForm.getValues('createdById');
    if (!current) createForm.setValue('createdById', me.id);
  }, [isEdit, me?.id, createForm]);

  // Create mode: a blank / cleared assignee resolves to the signed-in user
  // for SALES_AGENT (picker hidden) and SALES_LEAD (must pick self or a
  // report). An ADMIN may leave it blank on purpose: the lead is then sent
  // through the Lead Parsing round-robin, which gives it to the next
  // destination expert, the same way a website lead is dealt out. Options
  // come from GET /users/assignable-agents (hierarchy-scoped server-side).
  const canLeaveUnassigned = isAdmin;
  const watchedAssignedAgentId = createForm.watch('assignedAgentId');
  useEffect(() => {
    if (isEdit || !me?.id || canLeaveUnassigned) return;
    if (watchedAssignedAgentId) return;
    createForm.setValue('assignedAgentId', me.id);
    const branchId = agents.branchByAgentId.get(me.id);
    if (branchId) createForm.setValue('branchId', branchId);
  }, [
    isEdit,
    me?.id,
    canLeaveUnassigned,
    watchedAssignedAgentId,
    agents.branchByAgentId,
    createForm,
  ]);

  // Once assignable agents load, keep the create picker honest: if the
  // current value isn't in the caller's scoped list, fall back to self
  // (or to blank, where blank is allowed).
  useEffect(() => {
    if (isEdit) return;
    if (!me?.id || agents.isLoading) return;
    const allowed = new Set(agents.options.map((o) => o.value));
    const current = createForm.getValues('assignedAgentId');
    if (current && allowed.has(current)) return;
    if (!current && canLeaveUnassigned) return;
    createForm.setValue('assignedAgentId', canLeaveUnassigned ? null : me.id);
    if (canLeaveUnassigned) return;
    const branchId = agents.branchByAgentId.get(me.id);
    if (branchId) createForm.setValue('branchId', branchId);
  }, [
    isEdit,
    me?.id,
    canLeaveUnassigned,
    agents.isLoading,
    agents.options,
    agents.branchByAgentId,
    createForm,
  ]);

  // 2026-06-24: Branch field was hidden from the lead form (auto-fill
  // only). The AgentSelect path already seeds `branchId` from the
  // picked agent's branch for ADMIN / SALES_LEAD; the
  // self-assign path (SALES_AGENT) needs the same seed since the
  // picker doesn't fire on a programmatic setValue. Resolves via
  // `agents.branchByAgentId` once the agents list loads, so the
  // submit payload carries the agent's branch.
  useEffect(() => {
    if (isEdit) return;
    if (!me?.id) return;
    if (agents.isLoading) return;
    const current = createForm.getValues('branchId');
    if (current) return;
    const next = agents.branchByAgentId.get(me.id) ?? null;
    if (next) createForm.setValue('branchId', next);
  }, [isEdit, me?.id, agents.isLoading, agents.branchByAgentId, createForm]);

  // Default budget currency to INR on create. Most enquiries are domestic
  // (legacy data is ~95% INR); the operator can still pick another currency
  // from the Currency master dropdown. Skipped on edit so we don't override
  // a lead that was deliberately captured in a different currency.
  useEffect(() => {
    if (isEdit) return;
    const current = createForm.getValues('budgetCurrency');
    if (!current) createForm.setValue('budgetCurrency', 'INR');
  }, [isEdit, createForm]);

  // ----- Customer state ↔ RHF state mirror (create mode only) -----
  // The customer inline-form lives in local `useState` (`customerForm`) for
  // ergonomic typing - but the Zod schema's
  //   `.refine(v => Boolean(v.customerId) || Boolean(v.customer), …)`
  // check runs against the RHF state. Without this mirror, RHF never sees
  // the typed values and the "Pick an existing customer or fill in the
  // customer details" error fires even after the operator filled
  // everything in.
  //
  // We deliberately leave `customer` as `undefined` while the inline form
  // is COMPLETELY blank, so the friendlier refine error surfaces instead
  // of a wall of per-field "min length 1" errors before the user has
  // touched a single input.
  useEffect(() => {
    if (isEdit) return;
    if (customer) {
      createForm.setValue('customerId', customer.id);
      createForm.setValue('customer', undefined);
      return;
    }
    createForm.setValue('customerId', undefined);
    const anyInput =
      customerForm.firstName.trim() ||
      customerForm.lastName.trim() ||
      customerForm.primaryName.trim() ||
      customerForm.email.trim() ||
      customerForm.mobileNumber.trim() ||
      customerForm.altPhone.trim() ||
      customerForm.alternateEmail.trim();
    if (!anyInput) {
      createForm.setValue('customer', undefined);
      return;
    }
    createForm.setValue('customer', {
      firstName: customerForm.firstName.trim(),
      lastName: customerForm.lastName.trim() || null,
      primaryName: customerForm.primaryName.trim() || null,
      email: customerForm.email.trim() || null,
      mobileCountry: customerForm.mobileCountry.trim() || null,
      mobileNumber: customerForm.mobileNumber.trim(),
      altPhone: customerForm.altPhone.trim() || null,
      // 2026-06-27. Carry the alternate email + "mark as primary"
      // intent through to the server's customer-upsert. The lead's
      // `customerEmail` snapshot below still uses the typed primary;
      // the swap (if any) happens customer-side only.
      alternateEmail: customerForm.alternateEmail.trim() || null,
      markEmailAsPrimary:
        customerForm.markEmailAsPrimary && customerForm.alternateEmail.trim().length > 0,
    });
  }, [isEdit, customer, customerForm, createForm]);

  // Auto-title sync (create mode only). The "Lead title" input was
  // removed from the UI in favour of a server-friendly auto-generated
  // value, but the create schema still requires `min(2)` chars - if we
  // wait until submit to fill it (the original approach), zod rejects
  // the payload BEFORE the submit handler runs, and because the field
  // is hidden the operator sees nothing happen. So we keep `title` in
  // sync with the relevant inputs continuously, so the form is always
  // valid by the time the operator hits "Create lead".
  //
  // The deps deliberately include the customer state + leadCategory +
  // destinationId so the title refreshes whenever the operator types
  // a name, picks a destination, or flips category.
  const watchedDestinationId = createForm.watch('destinationId');
  const watchedLeadCategory = createForm.watch('leadCategory');
  useEffect(() => {
    if (isEdit) return;
    const nextTitle = buildAutoTitle({
      customerDisplay: customer
        ? customer.displayName
        : `${customerForm.firstName ?? ''} ${customerForm.lastName ?? ''}`.trim(),
      destinationLabel:
        (destinations.options.find((o) => o.value === watchedDestinationId)?.label ?? null) || null,
      leadCategory: (watchedLeadCategory as 'DOMESTIC' | 'INTERNATIONAL' | null) ?? null,
    });
    const current = createForm.getValues('title');
    if (current !== nextTitle) {
      createForm.setValue('title', nextTitle, { shouldValidate: false });
    }
  }, [
    isEdit,
    customer,
    customerForm.firstName,
    customerForm.lastName,
    watchedDestinationId,
    watchedLeadCategory,
    destinations.options,
    createForm,
  ]);

  // 2026-06-27. When the operator picks a Marketing Destination on
  // CREATE, mirror the choice into the regular Destination field
  // (operators almost always want them to match - the marketing tag
  // IS the destination unless they intentionally diverge). They can
  // still override afterwards. Skipped on edit because Marketing
  // Destination is locked there anyway, and edit-mode mirroring
  // would overwrite intentional historical divergence.
  useEffect(() => {
    if (isEdit) return;
    if (!watchedDestinationId) return;
    const currentDest = createForm.getValues('destinationCityId');
    if (currentDest === watchedDestinationId) return;
    createForm.setValue('destinationCityId', watchedDestinationId, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [isEdit, watchedDestinationId, createForm]);

  // 2026-07-07. Symmetric mirror in the OTHER direction: when the
  // operator picks a Destination on CREATE, mirror the choice into
  // the Marketing Destination field. Both fields draw from the same
  // destinations master (Masters > Destinations), so keeping them in
  // sync means the operator only has to touch one picker in the common
  // case. They can override Marketing Destination afterwards if it
  // should diverge (rare - marketing tag usually equals destination).
  // Guarded on same-value + edit-mode + non-empty for the same reasons
  // as the reverse effect above; the same-value check prevents the
  // two effects from ping-ponging.
  const watchedDestinationCityId = createForm.watch('destinationCityId');
  useEffect(() => {
    if (isEdit) return;
    if (!watchedDestinationCityId) return;
    const currentMarketing = createForm.getValues('destinationId');
    if (currentMarketing === watchedDestinationCityId) return;
    createForm.setValue('destinationId', watchedDestinationCityId, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [isEdit, watchedDestinationCityId, createForm]);

  // Hydrate the edit form when the lead loads or saves (updatedAt
  // changes). Skip background refetches so operator toggles survive.
  useEffect(() => {
    if (!isEdit || !leadQuery.data) return;
    const lead = leadQuery.data;
    const version = `${lead.id}:${lead.updatedAt}`;
    if (hydratedLeadVersionRef.current === version) return;
    hydratedLeadVersionRef.current = version;

    setCustomer(toListItem(lead));
    // Show the same inline-fields layout as create mode (just disabled
    // for the customer-side fields, since editing customer master from
    // a lead PATCH would be cross-domain). Picker stays available for
    // the linked-customer reference but defaults to hidden.
    setShowPicker(false);
    const split = parseDisplayName(lead.customer.displayName);
    setCustomerForm({
      // 2026-06-25: hydrate from the lead's customer snapshot when
      // present, falling back to the customer relation for legacy
      // leads created before the columns existed.
      firstName: lead.customerFirstName ?? split.first ?? '',
      lastName: lead.customerLastName ?? split.last ?? '',
      primaryName: lead.customer.primaryName ?? '',
      email: lead.customerEmail ?? lead.customer.email ?? '',
      mobileCountry: lead.customerMobileCountry ?? '91',
      mobileNumber: lead.customerMobileNumber ?? lead.customer.mobileNumber ?? '',
      altPhone: lead.customer.altPhone ?? '',
      // Show the promoted / pending alternate in the UI - not the DB
      // alternate column after a swap (that holds the former primary).
      alternateEmail: deriveAlternateEmailDisplay(lead),
      markEmailAsPrimary: deriveMarkEmailAsPrimary(lead),
      // Hydrate companyName from the customer (when CORPORATE).
      companyName: lead.customer.companyName ?? '',
    });
    // Hydrate isCorporate from the loaded customer's kind so the
    // edit-mode picker reflects current state.
    setIsCorporate(lead.customer.kind === 'CORPORATE');
    // Seed the picker's display label from the loaded lead so the
    // selected-state tile renders without an extra round trip.
    // Falls back to the import-captured Customer origin city when the
    // working departure was never picked — the operator sees it
    // pre-filled and the next save persists it (kept in sync with the
    // `departureCityId` fallback in the reset below).
    setDepartureCityLabel(lead.departureCity?.name ?? lead.leadDepartureCity?.name ?? null);
    // `setDestinationCityLabel` was removed 2026-06-24 - destination
    // is now a destinations-master select, not a city search panel.
    // Seed manual-stage-move state from the loaded lead. `original*`
    // snapshots the load-time values so we only fire the transition
    // when the operator actually changes them.
    // 2026-07-09. `initialStatusIdOverride` / `initialSubStatusIdOverride`
    // let the modal host preseed the inner pickers to the outer header
    // picker's target (e.g. "Active" / "Proposal") so a Save Changes
    // click will fire the transition in the same submit round-trip.
    setStatusId(initialStatusIdOverride ?? lead.currentStatus.id);
    setOriginalStatusId(lead.currentStatus.id);
    setSubStatusId(
      initialSubStatusIdOverride !== undefined
        ? initialSubStatusIdOverride
        : (lead.currentSubStatus?.id ?? null),
    );
    setOriginalSubStatusId(lead.currentSubStatus?.id ?? null);
    updateForm.reset({
      title: lead.title,
      customerId: lead.customer.id,
      queryTypeId: lead.queryType.id,
      querySubSourceId: lead.querySubSource?.id ?? null,
      queryMediumId: lead.queryMedium?.id ?? null,
      priority: lead.priority,
      leadCategory: lead.leadCategory ?? null,
      assignedAgentId: lead.assignedAgent?.id ?? null,
      branchId: lead.branch?.id ?? null,
      // Working departure falls back to the import-captured customer
      // origin (a City master ref) when it was never picked, so imported
      // leads land in the move-to-Active modal with Departure city
      // pre-filled instead of forcing a re-pick of the same city.
      departureCityId: lead.departureCity?.id ?? lead.leadDepartureCity?.id ?? null,
      // 2026-06-24: Destination is mandatory in both create AND edit.
      // Hydrate legacy leads (where destinationCity is missing) with
      // an empty string instead of null so the form's UuidSchema
      // surfaces the "Invalid UUID" error on submit and the operator
      // is forced to pick before save. Picked-then-hydrated leads
      // round-trip unchanged.
      // 2026-06-27. Undefined (not `''`) when the lead has no
      // destination yet - empty string would fail `z.uuid()` on the
      // next PATCH submit.
      destinationCityId: lead.destinationCity?.id ?? undefined,
      destinationId: lead.destination?.id ?? null,
      budgetAmount: lead.budgetAmount,
      budgetCurrency: lead.budgetCurrency,
      expectedTravelStart: lead.expectedTravelStart,
      expectedTravelEnd: lead.expectedTravelEnd,
      travellerAdults: lead.travellerAdults,
      travellerChildren: lead.travellerChildren,
      travellerInfants: lead.travellerInfants,
      childAges: lead.childAges ?? [],
      notes: lead.notes,
      // Backing field for the "Is reverted to customer?" select.
      // Bi-state: true = Yes, false = No (default for new leads).
      isRevertedToCustomer: lead.isRevertedToCustomer,
      // Operator-editable until the Itinerary module ships.
      isItinerarySent: lead.isItinerarySent,
      // ----- ADR-0035 legacy parity fields -----
      queryDate: lead.queryDate ?? null,
      querySourceUrl: lead.querySourceUrl ?? null,
      ttvAmount: lead.ttvAmount ?? null,
      ttvCurrency: lead.ttvCurrency ?? null,
      preferredHotelCategory: lead.preferredHotelCategory ?? null,
      isFirstTimeTraveler: lead.isFirstTimeTraveler ?? null,
      isHoneymoonCouple: lead.isHoneymoonCouple ?? null,
      hasValidPassport: lead.hasValidPassport ?? null,
      isDecisionMaker: lead.isDecisionMaker ?? null,
      bookingTimeline: lead.bookingTimeline ?? null,
      isFlightBooked: lead.isFlightBooked ?? null,
      preferredTravelMode: lead.preferredTravelMode ?? null,
      mealPlanId: lead.mealPlan?.id ?? null,
      expectedPaymentDate: lead.expectedPaymentDate ?? null,
      requirementSummary: lead.requirementSummary ?? null,
    });
    // `querySourceId` is locked on edit (UpdateLeadRequestSchema omits it),
    // but we still want the user to SEE the existing source value in the
    // disabled dropdown. Set it on the loose-typed form state for display
    // only; it's never read back by the submit handler.
    (updateForm as unknown as { setValue: (n: string, v: unknown) => void }).setValue(
      'querySourceId',
      lead.querySource?.id ?? null,
    );
    // Default initial status on edit not changed via this form - see the
    // stage transition dialog. The schema doesn't include status anyway.
  }, [isEdit, leadQuery.data, updateForm]);

  // Cast to AnyForm so the loose-typed helpers (Controlled / NumberControlled)
  // accept the union - both forms have the same RHF shape at runtime; the
  // generic differences (create has required fields, update is partial) are
  // already enforced by zod-resolver at submit time.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const form = (isEdit ? updateForm : createForm) as ReturnType<typeof useForm<any>>;
  const pending = create.isPending || update.isPending;

  // 2026-06-25: the "Is reverted to customer?" plumbing
  // (watchedIsReverted / isRevertedValue / onRevertedChange /
  // revertHint) was removed with the picker. The flag is now
  // managed by the Move-to-Active followup flow + the server-side
  // derivation (Active + Proposal + !isItinerarySent). The
  // underlying `isRevertedToCustomer` form-state field is still
  // hydrated on edit so its current value round-trips on PATCH
  // without being clobbered.

  const submitCreate = createForm.handleSubmit(async (values) => {
    if (isCorporate && !customerForm.companyName.trim()) {
      toast.error('Corporate name is required when marking as a corporate customer.');
      return;
    }
    // 2026-06-27. Belt-and-braces: strip empty-string UUID fields
    // BEFORE zodResolver runs them through `z.uuid()`. A
    // `Controlled` (UzSelect) that the operator never picked yields
    // an empty string in form state; zod's `z.uuid()` then throws
    // "Invalid UUID" - which reads as a developer error when the
    // real cause is "operator left a required dropdown blank". By
    // promoting "" to undefined we let the schema's
    // required/optional rules fire their natural messages instead.
    const sanitised = sanitiseUuidLikeFields(values);
    // The customer-state mirror useEffect above keeps `values.customer` /
    // `values.customerId` in sync with the local UI, so by the time we reach
    // here zod has already validated the full payload. We just need to pick
    // which side of the union to send. The picker path strips any
    // half-typed inline fields; the inline path strips a stale customerId.
    const payload: CreateLeadRequest = customer
      ? { ...sanitised, customerId: customer.id, customer: undefined }
      : { ...sanitised, customerId: undefined };
    // Belt-and-braces (2026-07 adults-reset): re-read pax from live form
    // state so a stale resolver snapshot can't silently ship the UI
    // defaults (1 adult / 0 children) after the operator typed 2/2.
    const adults = createForm.getValues('travellerAdults');
    const children = createForm.getValues('travellerChildren');
    const infants = createForm.getValues('travellerInfants');
    if (typeof adults !== 'number' || typeof children !== 'number' || typeof infants !== 'number') {
      toast.error('Adults, children and infants must be set to a number before saving.');
      return;
    }
    payload.travellerAdults = adults;
    payload.travellerChildren = children;
    payload.travellerInfants = infants;
    if (!payload.assignedAgentId && canLeaveUnassigned) {
      // Explicit null: the server reads "omitted" as "the creator owns it"
      // and null as "nobody yet - let Lead Parsing round-robin assign it".
      payload.assignedAgentId = null;
    } else if (!payload.assignedAgentId && me?.id) {
      payload.assignedAgentId = me.id;
      if (!payload.branchId) {
        const branchId = agents.branchByAgentId.get(me.id);
        if (branchId) payload.branchId = branchId;
      }
    }
    // Title is hidden from the create UI. Auto-derive a sensible
    // placeholder so the server's `min(2)` validation passes. Falls
    // back to "New lead" if we have no signal yet.
    if (!payload.title || payload.title.trim().length < 2) {
      payload.title = buildAutoTitle({
        customerDisplay: customer
          ? customer.displayName
          : `${customerForm.firstName ?? ''} ${customerForm.lastName ?? ''}`.trim(),
        destinationLabel:
          (destinations.options.find((o) => o.value === values.destinationId)?.label ?? null) ||
          null,
        leadCategory: (values.leadCategory as 'DOMESTIC' | 'INTERNATIONAL' | null) ?? null,
      });
    }
    try {
      const lead = await create.mutateAsync(payload);
      const customerId = customer?.id ?? lead.customer.id;
      if (isCorporate && customerId) {
        await updateCustomer
          .mutateAsync({
            id: customerId,
            payload: {
              kind: 'CORPORATE',
              companyName: customerForm.companyName.trim(),
            },
          })
          .catch(() => {
            // toast already fired; lead was created - operator can fix in /customers/:id
          });
      }
      if (onSaveSuccess) onSaveSuccess(lead.id, lead);
      else navigate(`/leads/${lead.id}`);
    } catch {
      // toast already fired
    }
  }, scrollToFirstError);

  const submitUpdate = updateForm.handleSubmit(async (rawValues) => {
    if (!id) return;
    if (isCorporate && !customerForm.companyName.trim()) {
      toast.error('Corporate name is required when marking as a corporate customer.');
      return;
    }
    // 2026-06-27. Same empty-string-UUID guard as the create path.
    // A `Controlled` whose value was cleared yields `''` in form
    // state; running that through `z.uuid()` on the next PATCH
    // throws "Invalid UUID" with no field name. Strip first.
    const values = sanitiseUuidLikeFields(rawValues);
    try {
      // 2026-07-08. Save order rebalanced:
      //   1. Customer PATCH (unchanged)
      //   2. Lead field PATCH (moved earlier)
      //   3. Stage transition (moved to the end)
      // Previously the transition fired first, but the server-side
      // active-readiness gate (`lead_not_active_ready`) reads the
      // lead's DB state before the PATCH has landed - so a
      // not_qualified -> active move made in the same edit as filling
      // Travel start / end / Budget 400'd every time. Doing the PATCH
      // first lets the transition read the persisted values. LOST +
      // WON still validate their own side-inputs (lostReasonId /
      // won-conversion set) via their picker flows, so the earlier
      // "bail out before saving the rest" concern (partial persistence
      // for a rejected LOST transition) is moot - those move via the
      // LeadStatusChanger picker on the detail page, not this form.
      const stageChanged =
        statusId !== originalStatusId || (subStatusId ?? null) !== (originalSubStatusId ?? null);
      // 1. PATCH the customer when one of the customer-side fields
      //    changed. 2026-06-25 expanded set: altPhone (legacy), kind
      //    (isCorporate toggle), companyName (when corporate),
      //    alternateEmail (persisted), and the email-swap path
      //    (when the operator typed an alternateEmail AND ticked
      //    "Mark as primary"). The rest of the customer record is
      //    still managed via /customers/:id.
      const customerId = customer?.id ?? leadQuery.data?.customer.id ?? null;
      const loadedAltPhone = leadQuery.data?.customer.altPhone ?? '';
      const currentAltPhone = customerForm.altPhone.trim();
      const altPhoneChanged = currentAltPhone !== (loadedAltPhone ?? '').trim();
      const loadedKind = leadQuery.data?.customer.kind ?? 'INDIVIDUAL';
      const nextKind = isCorporate ? 'CORPORATE' : 'INDIVIDUAL';
      const kindChanged = nextKind !== loadedKind;
      const loadedCompanyName = leadQuery.data?.customer.companyName ?? '';
      const currentCompanyName = customerForm.companyName.trim();
      const companyNameChanged = currentCompanyName !== (loadedCompanyName ?? '').trim();
      const loadedAltEmail = leadQuery.data?.customer.alternateEmail ?? '';
      const loadedPrimaryName = leadQuery.data?.customer.primaryName ?? '';
      const loadedAlternateDisplay = leadQuery.data
        ? deriveAlternateEmailDisplay(leadQuery.data)
        : loadedAltEmail;
      const currentAltEmail = customerForm.alternateEmail.trim();
      const altEmailChanged = currentAltEmail !== loadedAlternateDisplay.trim();
      const currentPrimaryName = customerForm.primaryName.trim();
      const primaryNameChanged = currentPrimaryName !== loadedPrimaryName.trim();
      const loadedPrimaryEmail = leadQuery.data?.customer.email ?? '';
      const intakeEmail = leadQuery.data ? getLeadIntakeEmail(leadQuery.data) : '';
      const alreadyPromoted = leadQuery.data ? isPrimaryEmailPromoted(leadQuery.data) : false;
      const promoteEmail = customerForm.markEmailAsPrimary && currentAltEmail.length > 0;
      // When "Mark as primary" is ticked we swap on the customer row:
      // customer.email becomes the typed alternate; customer.alternateEmail
      // becomes the previous primary. The lead intake snapshot is untouched
      // and the form keeps showing intake + alternate in place after reload.
      const customerPatch: Record<string, unknown> = {};
      if (altPhoneChanged) {
        customerPatch.altPhone = currentAltPhone === '' ? null : currentAltPhone;
      }
      if (primaryNameChanged) {
        customerPatch.primaryName = currentPrimaryName === '' ? null : currentPrimaryName;
      }
      if (kindChanged) {
        customerPatch.kind = nextKind as 'INDIVIDUAL' | 'CORPORATE';
      }
      if (companyNameChanged) {
        customerPatch.companyName = currentCompanyName === '' ? null : currentCompanyName;
      }
      if (promoteEmail && !alreadyPromoted) {
        customerPatch.email = currentAltEmail;
        customerPatch.alternateEmail = loadedPrimaryEmail || intakeEmail || null;
      } else if (
        promoteEmail &&
        alreadyPromoted &&
        currentAltEmail.localeCompare(loadedPrimaryEmail.trim(), undefined, {
          sensitivity: 'accent',
        }) !== 0
      ) {
        // Operator changed the promoted address to a different one.
        customerPatch.email = currentAltEmail;
        customerPatch.alternateEmail = loadedPrimaryEmail || null;
      } else if (!promoteEmail && altEmailChanged) {
        customerPatch.alternateEmail = currentAltEmail === '' ? null : currentAltEmail;
      }
      // 2026-07-09. Compound-flow detection identical to the lead
      // PATCH's suppress-toast rule below: silence the "Customer
      // updated" toast when the same submit is going to chain a lead
      // PATCH (which itself always runs), a stage transition, or a
      // destination approval so the operator sees ONE final outcome
      // message. The lead PATCH always follows the customer PATCH so
      // in practice we can just check "there's a following mutation"
      // (which is true here whenever customer + lead both run) OR
      // embedded / stage change.
      const customerAndLeadBothRun = Object.keys(customerPatch).length > 0;
      const isCompoundCustomerFlow =
        embedded || (stageChanged && Boolean(statusId)) || customerAndLeadBothRun;
      if (customerId && customerAndLeadBothRun) {
        await updateCustomer
          .mutateAsync({
            id: customerId,
            payload: customerPatch,
            suppressSuccessToast: isCompoundCustomerFlow,
          })
          .catch(() => {
            // toast already fired by the hook; don't block the lead
            // PATCH below if the customer update fails - the operator
            // can retry from /customers/:id.
          });
      }
      // 2. Save the rest of the field-level changes via PATCH. We
      //    layer the lead-row customer snapshot fields on top of
      //    the form payload so name / phone edits in this form
      //    propagate to the denormalized columns on `leads` too.
      //    The lead's `email` column is NOT touched here - it's the
      //    intake snapshot and stays as-is even when the operator
      //    promotes the alternate email to the customer record.
      // 2026-07-09. Sales-agent destination edits go through the
      // "Change destination" dialog (POST /status-change-requests
      // kind=DESTINATION_CHANGE). The destination Controlled is
      // read-only for that role now, so `values.destinationCityId`
      // won't have drifted here in the normal flow. We still strip
      // it defensively - a stale form state or a future refactor
      // that widens the field's disabled logic must never quietly
      // move the destination without an approval trail.
      const destinationChanged =
        values.destinationCityId !== undefined &&
        values.destinationCityId !== originalDestinationCityId;
      const destinationNeedsApproval = isAgentOrLead && destinationChanged;
      const patchPayload: UpdateLeadRequest = {
        ...values,
        ...(customer ? { customerId: customer.id } : {}),
        customerFirstName: customerForm.firstName.trim() || null,
        customerLastName: customerForm.lastName.trim() || null,
        customerMobileCountry: customerForm.mobileCountry || null,
        customerMobileNumber: customerForm.mobileNumber.trim() || null,
      };
      // Same adults-reset guard as create: force-read live pax so a
      // NumberControlled edit can't be dropped from the PATCH body.
      const adults = updateForm.getValues('travellerAdults');
      const children = updateForm.getValues('travellerChildren');
      const infants = updateForm.getValues('travellerInfants');
      if (typeof adults === 'number') patchPayload.travellerAdults = adults;
      if (typeof children === 'number') patchPayload.travellerChildren = children;
      if (typeof infants === 'number') patchPayload.travellerInfants = infants;
      if (destinationNeedsApproval) {
        delete patchPayload.destinationCityId;
      }
      // 2026-07-09. Suppress the standalone "Lead updated" toast when
      // this PATCH is part of a compound flow so the operator sees
      // exactly ONE final outcome message instead of overlapping
      // "Lead updated" + a follow-on "Still missing" / transition
      // error. Fires the toast only for the plain edit case (no
      // embedded host, no stage transition chained, no destination
      // approval to submit afterwards).
      const isCompoundFlow =
        embedded || (stageChanged && Boolean(statusId)) || destinationNeedsApproval;
      let lead = await update.mutateAsync({
        id,
        payload: patchPayload,
        suppressSuccessToast: isCompoundFlow,
      });
      // 3. Now that the field values are persisted, fire the stage
      //    transition. Any server-side readiness check (e.g. active)
      //    sees the just-saved values. Success writes the freshest
      //    lead into the React Query cache; we use that below when
      //    handing back to the host via onSaveSuccess so the host
      //    (LeadStatusChanger's missing-fields modal) can re-check
      //    predicates without a second network round-trip.
      if (stageChanged && statusId) {
        const transitioned = await transitionStage.mutateAsync({
          id,
          payload: { toStatusId: statusId, toSubStatusId: subStatusId ?? null },
        });
        lead = transitioned;
      }
      if (destinationNeedsApproval && values.destinationCityId) {
        // 2026-07-09. Defensive fallback: if a sales agent somehow
        // reaches save with a changed destination (form-state drift,
        // React DevTools override, unknown regression), route through
        // the same approval endpoint the manual "Change destination"
        // button uses. The picker itself is disabled for this role now,
        // so this branch should rarely fire.
        await requestApproval.mutateAsync({
          id,
          payload: {
            kind: 'DESTINATION_CHANGE',
            toDestinationId: values.destinationCityId,
          },
        });
        updateForm.setValue('destinationCityId', originalDestinationCityId ?? undefined, {
          shouldDirty: false,
        });
      }
      if (onSaveSuccess) onSaveSuccess(lead.id, lead);
      else navigate(`/leads/${lead.id}`);
    } catch {
      // toast already fired
    }
  }, scrollToFirstError);

  const onSubmit = isEdit ? submitUpdate : submitCreate;

  if (isEdit && leadQuery.isLoading) {
    return (
      <PageShell title="Loading lead…" description="">
        <UzCard>
          <div className="flex items-center justify-center gap-2 p-8 text-sm text-zinc-500">
            <UzSpinner size="sm" /> Fetching lead detail…
          </div>
        </UzCard>
      </PageShell>
    );
  }

  if (isEdit && (leadQuery.isError || !leadQuery.data)) {
    return (
      <PageShell title="Lead not found" description="">
        <UzCard>
          <div className="space-y-3 p-8 text-center">
            <div className="text-zinc-600">
              We couldn&apos;t load this lead. It may have been deleted, or the URL is wrong.
            </div>
            <div className="flex justify-center">
              <UzButton variant="outline" onClick={backToList}>
                <ArrowLeft className="mr-1.5 h-4 w-4" />
                Back to lead inbox
              </UzButton>
            </div>
          </div>
        </UzCard>
      </PageShell>
    );
  }

  // Same rule the lead-detail header uses to hide its Edit button: a
  // sales agent can't edit a lead that's closed as Won or Lost until the
  // status moves out of that bucket (which needs a team-lead approval).
  // Repeated here so typing the /edit URL lands on the explanation
  // instead of a form that can't be saved.
  const closedBucket = leadQuery.data?.currentBucket ?? null;
  if (isEdit && !isAdmin && (closedBucket === 'LOST' || closedBucket === 'WON')) {
    return (
      <PageShell title="Lead is closed" description="">
        <UzCard>
          <div className="space-y-3 p-8 text-center">
            <div className="text-zinc-600">
              This lead is marked {closedBucket === 'WON' ? 'Won' : 'Lost'}, so it can&apos;t be
              edited. Reopen it from the lead detail page first - a team lead has to approve the
              change.
            </div>
            <div className="flex justify-center">
              <UzButton variant="outline" onClick={() => navigate(`/leads/${id}`)}>
                <ArrowLeft className="mr-1.5 h-4 w-4" />
                Back to lead detail
              </UzButton>
            </div>
          </div>
        </UzCard>
      </PageShell>
    );
  }

  // The form body lives in a single block so both the page route and the
  // embedded (modal) caller share the same JSX. The PageShell wrapper +
  // header actions are only rendered when NOT embedded; the host modal
  // supplies its own title / description / footer.
  const formBody = (
    <div className={embedded ? 'space-y-6' : 'mx-auto max-w-4xl space-y-6'}>
      <UzCard
        title="Customer"
        description={
          showPicker
            ? 'Pick an existing customer to link this enquiry to.'
            : "Enter the customer's details. We'll create a new customer record, or link to an existing one if the email or phone matches."
        }
        actions={
          !isEdit && (
            <button
              type="button"
              className="text-xs font-medium text-blue-600 hover:text-blue-700"
              onClick={() => {
                setShowPicker((v) => !v);
                if (showPicker) handleCustomerChange(null);
              }}
            >
              {showPicker ? 'Enter customer details instead' : 'Link existing customer'}
            </button>
          )
        }
      >
        {showPicker ? (
          <CustomerPicker
            value={customer}
            onChange={handleCustomerChange}
            disabled={pending}
            required
            // Customer can't be changed once a lead exists - the FK is the
            // canonical link and re-pointing it would orphan the existing
            // follow-up / stage history. In create mode `readOnly` is false
            // so the picker stays fully interactive.
            readOnly={isEdit}
            error={
              form.formState.errors.customerId?.message?.toString() ??
              form.formState.errors.customer?.message?.toString()
            }
          />
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {/* 2026-06-25: First / last name, mobile country, mobile
                  number are now editable in edit mode too. Email is the
                  one exception - it stays disabled (the intake snapshot
                  is preserved); the "Mark as primary email" path
                  below promotes the alternate value to the customer
                  record instead. */}
              {/* 2026-06-25 (ops): on EDIT mode, only the two
                  "alternate" fields below (alt phone + alt email)
                  stay editable. Everything else - first/last name,
                  email, mobile - is locked so operators can't
                  accidentally clobber the customer master from the
                  lead form. Corporate fields live below the grid. */}
              <UzInput
                name="customer-firstName"
                label="First name"
                required
                placeholder="Vansh"
                autoFocus={!isEdit}
                value={customerForm.firstName}
                onChange={(e) => setCustomerField('firstName', e.target.value)}
                disabled={pending || isEdit}
                className={isEdit ? DISABLED_INPUT_CLS : undefined}
              />
              <UzInput
                name="customer-lastName"
                label="Last name"
                placeholder="Goyal"
                value={customerForm.lastName}
                onChange={(e) => setCustomerField('lastName', e.target.value)}
                disabled={pending || isEdit}
                className={isEdit ? DISABLED_INPUT_CLS : undefined}
              />
              <UzInput
                name="customer-email"
                type="email"
                label="Email"
                // Required on create only. In edit mode the input is disabled
                // (it shows the intake snapshot - the customer's live email is
                // changed via "Alternate email" + "Mark as primary"), so a
                // required marker there would be misleading.
                required={!isEdit}
                placeholder="name@example.com"
                value={customerForm.email}
                onChange={(e) => setCustomerField('email', e.target.value)}
                disabled={pending || isEdit}
                className={isEdit ? DISABLED_INPUT_CLS : undefined}
                hint={
                  isEdit
                    ? 'Intake snapshot - to change the customer email use "Alternate email" below + "Mark as primary".'
                    : 'Used to match against an existing customer (email or phone).'
                }
              />
              <UzInput
                name="customer-primaryName"
                label="Primary name"
                placeholder="e.g. John Doe"
                value={customerForm.primaryName}
                onChange={(e) => setCustomerField('primaryName', e.target.value)}
                disabled={pending}
                hint={
                  customerForm.primaryName.trim().length > 0
                    ? 'Used for itinerary share pages + greeting in published emails.'
                    : 'Optional. If empty, we fall back to the customer name.'
                }
              />
              <div className="grid grid-cols-[140px_1fr] gap-2">
                <CountryCodePicker
                  value={customerForm.mobileCountry}
                  onChange={(dial) => setCustomerField('mobileCountry', dial)}
                  disabled={pending || isEdit}
                />
                <UzInput
                  name="customer-mobileNumber"
                  label="Preferred contact #"
                  placeholder="9876543210"
                  required
                  type="tel"
                  inputMode="numeric"
                  value={customerForm.mobileNumber}
                  onChange={(e) =>
                    setCustomerField('mobileNumber', e.target.value.replace(/\D+/g, ''))
                  }
                  disabled={pending || isEdit}
                  className={isEdit ? DISABLED_INPUT_CLS : undefined}
                />
              </div>
              {/* Alternate contact is the ONE customer-side field
                    operators can edit from the lead form (the rest
                    must go through /customers/:id). Captured at lead
                    intake too so the agent has a second phone the
                    moment the lead lands. */}
              <UzInput
                name="customer-altPhone"
                label="Alternate contact #"
                placeholder="9876543210"
                type="tel"
                inputMode="tel"
                value={customerForm.altPhone}
                onChange={(e) => setCustomerField('altPhone', e.target.value)}
                disabled={pending}
                hint={isEdit ? undefined : 'Optional second phone - kept on the customer record.'}
              />
              {/* 2026-06-25: Alternate email + "Mark as primary"
                  checkbox stacked in a single grid cell so the
                  checkbox sits directly under its email field
                  instead of orphaned in an empty column. */}
              <div className="flex flex-col gap-2">
                <UzInput
                  name="customer-alternateEmail"
                  type="email"
                  label="Alternate email"
                  placeholder="newaddress@example.com"
                  value={customerForm.alternateEmail}
                  onChange={(e) => setCustomerField('alternateEmail', e.target.value)}
                  disabled={pending}
                  hint={
                    isEdit
                      ? 'Saved on the customer record. Tick the box below to make it the primary email.'
                      : 'Optional - kept on the customer record after create.'
                  }
                />
                {/* 2026-06-27. Visible on BOTH create and edit. The
                    server applies the swap when:
                    - CREATE: the inline customer payload carries
                      `markEmailAsPrimary: true` AND a non-empty
                      `alternateEmail`. The new customer is created
                      with `email = alternateEmail` and
                      `alternateEmail = email`. The lead's
                      `customerEmail` intake snapshot stays the
                      originally-typed primary.
                    - EDIT: the standalone customer PATCH swaps the
                      same way (existing logic). */}
                <label className="flex items-start gap-2 text-sm text-zinc-700">
                  <input
                    type="checkbox"
                    name="customer-markEmailAsPrimary"
                    className="mt-0.5 h-4 w-4 rounded border-zinc-300"
                    checked={customerForm.markEmailAsPrimary}
                    onChange={(e) => setCustomerField('markEmailAsPrimary', e.target.checked)}
                    disabled={pending || customerForm.alternateEmail.trim().length === 0}
                  />
                  <span>
                    <span className="font-medium">Mark as primary email</span>
                    <span className="block text-xs text-zinc-500">
                      {isEdit
                        ? "Swaps with the customer's current primary email on save. The lead's intake email above stays unchanged."
                        : "The customer will be created with this as their primary email; the email above is kept as the alternate. The lead's intake email stays unchanged."}
                    </span>
                  </span>
                </label>
              </div>
            </div>
            {(() => {
              // RHF's `customer` error can take two shapes depending on
              // which Zod check failed:
              //   - { message } when the top-level refine fails
              //     ("Pick an existing customer or fill in the customer
              //     details")
              //   - { firstName: { message }, mobileNumber: { message }, … }
              //     when individual field constraints fail (firstName
              //     min length, mobileNumber regex, …)
              // We surface both so the operator isn't left guessing why
              // the form refuses to submit.
              const e = form.formState.errors.customer as
                | {
                    message?: string;
                    firstName?: { message?: string };
                    lastName?: { message?: string };
                    primaryName?: { message?: string };
                    email?: { message?: string };
                    mobileNumber?: { message?: string };
                    mobileCountry?: { message?: string };
                  }
                | undefined;
              if (!e) return null;
              if (e.message) {
                return <p className="text-xs text-red-600">{e.message.toString()}</p>;
              }
              const parts: string[] = [];
              if (e.firstName?.message) parts.push(`First name: ${e.firstName.message}`);
              if (e.mobileNumber?.message) parts.push(`Mobile number: ${e.mobileNumber.message}`);
              if (e.email?.message) parts.push(`Email: ${e.email.message}`);
              if (e.mobileCountry?.message) parts.push(`Country: ${e.mobileCountry.message}`);
              if (e.primaryName?.message) parts.push(`Primary name: ${e.primaryName.message}`);
              if (e.lastName?.message) parts.push(`Last name: ${e.lastName.message}`);
              return parts.length > 0 ? (
                <p className="text-xs text-red-600">{parts.join(' · ')}</p>
              ) : null;
            })()}
          </div>
        )}
        {(!showPicker || customer) && (
          <div className="mt-3 grid grid-cols-1 gap-3 border-t border-zinc-100 pt-3 sm:grid-cols-2">
            <UzSelect
              name="customer-isCorporate"
              label="Is corporate customer?"
              options={YES_NO_OPTIONS}
              value={isCorporate ? 'true' : 'false'}
              onValueChange={(v) => {
                const next = v === 'true';
                setIsCorporate(next);
                if (!next) setCustomerField('companyName', '');
              }}
              disabled={pending}
            />
            {isCorporate && (
              <UzInput
                name="customer-companyName"
                label="Corporate name"
                placeholder="Acme Pvt Ltd"
                required
                value={customerForm.companyName}
                onChange={(e) => setCustomerField('companyName', e.target.value)}
                disabled={pending}
                hint="Required when corporate is Yes - saved on the customer record."
              />
            )}
          </div>
        )}
      </UzCard>

      <UzCard title="Enquiry">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Lead title is hidden from the UI. On create it's
                auto-derived at submit time (see `buildAutoTitle`); on
                edit it stays whatever the server has. The field is
                still in form state so the existing value round-trips
                cleanly on PATCH. */}
          {/* Manual stage move - edit mode only. New leads ALWAYS
                land at `not_qualified` (ADR-0038), so the picker would
                just confuse the operator. On edit the dropdown lists
                every active QueryStatus; the sub-status picker
                cascades and is hidden when the chosen status has no
                sub-statuses. Save fires `POST /leads/:id/transition`
                first so stage_history + outbox capture the move. */}
          {isEdit &&
            (() => {
              // Resolve hex for the currently-picked status/sub-status
              // so the colored "focus bar" beside each dropdown
              // reflects the master's color (or the bucket-derived
              // fallback when the master leaves it NULL).
              const pickedStatus = statusOptions.data.find((s) => s.id === statusId);
              const pickedSub = subStatusOptions.data.find((s) => s.id === subStatusId);
              // The master's `bucket` is a free-form string column but
              // `resolveStageHex` expects the LeadBucket enum; coerce
              // through the runtime check (unknown buckets fall back
              // to neutral via `resolveStageTone`).
              const parentBucket = (pickedStatus?.bucket ?? null) as 'OPEN' | 'WON' | 'LOST' | null;
              // Sub-statuses inherit the parent's color when their own
              // `color` is NULL - mirroring how the lead detail page
              // renders the stage chip.
              const subHex = pickedSub
                ? resolveStageHex(
                    pickedSub.color ?? pickedStatus?.color ?? null,
                    parentBucket,
                    pickedSub.name,
                  )
                : null;
              const savedStatus = leadQuery.data?.currentStatus ?? null;
              const savedSubStatus = leadQuery.data?.currentSubStatus ?? null;
              const savedStatusHex = savedStatus
                ? resolveStageHex(
                    savedStatus.color,
                    savedStatus.bucket as 'OPEN' | 'WON' | 'LOST',
                    savedStatus.name,
                  )
                : null;
              // 2026-07-16. Mirror the LeadStatusChanger gate from the detail
              // page here so Edit can't bypass it: while the lead is still
              // `not_qualified` with no COMPLETED-Connected follow-up, the status
              // can only move via the Follow-up flow (Call Status = Connected),
              // not this picker. The lock lifts automatically once a Connected
              // follow-up exists - identical rule/behaviour to the detail view.
              const statusLocked =
                savedStatus?.code === 'not_qualified' &&
                !(leadQuery.data?.followUps ?? []).some((f) => f.result === 'CONNECTED');
              return (
                <>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 sm:col-span-2">
                    <span className="text-xs font-medium text-zinc-600">Current status</span>
                    {savedStatus ? (
                      <UzBadge
                        tone={stageBadgeTone(
                          resolveStageTone(savedStatus.bucket, savedStatus.name),
                        )}
                        pill
                      >
                        {savedStatus.name}
                      </UzBadge>
                    ) : (
                      <span className="text-sm text-zinc-500">-</span>
                    )}
                    {savedSubStatus && (
                      <UzBadge tone="secondary" pill>
                        {savedSubStatus.name}
                      </UzBadge>
                    )}
                  </div>
                  <div
                    className="rounded-md border-l-4 pl-2 transition-colors"
                    style={{ borderLeftColor: savedStatusHex ?? 'transparent' }}
                    // Native tooltip surfaces the "why" while the picker is
                    // locked (matches the detail-page status changer).
                    title={
                      statusLocked
                        ? 'Log a Follow-up call with Call Status "Connected" to unlock this dropdown.'
                        : undefined
                    }
                  >
                    <UzSelect<string>
                      name="currentStatusId"
                      label="Query status"
                      placeholder={statusOptions.isLoading ? 'Loading…' : 'Select status'}
                      options={filteredStatusOptions}
                      // 2026-07-09. Always coerce null -> '' so the
                      // underlying Select stays CONTROLLED across the
                      // component's lifetime (was flipping to
                      // uncontrolled when statusId briefly cleared).
                      value={statusId ?? ''}
                      onValueChange={(v) => {
                        setStatusId(v ?? null);
                        setSubStatusId(null);
                      }}
                      disabled={pending || statusOptions.isLoading || statusLocked}
                      required
                      hint="Pick where to move this lead. Won and Long Follow-up are set by other flows."
                    />
                    {statusLocked && (
                      <span className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-800 ring-1 ring-amber-200">
                        <AlertTriangle className="h-3 w-3" aria-hidden />
                        Log a Connected follow-up to change status
                      </span>
                    )}
                  </div>
                  {(subStatusOptions.options.length > 0 || subStatusOptions.isLoading) && (
                    <div
                      className="rounded-md border-l-4 pl-2 transition-colors"
                      style={{ borderLeftColor: subHex ?? 'transparent' }}
                    >
                      <UzSelect<string>
                        name="currentSubStatusId"
                        label="Sub status"
                        placeholder={subStatusOptions.isLoading ? 'Loading…' : 'Select sub status'}
                        options={subStatusOptions.options}
                        value={subStatusId ?? ''}
                        onValueChange={(v) => setSubStatusId(v ?? null)}
                        disabled={pending || subStatusOptions.isLoading || statusLocked}
                      />
                    </div>
                  )}
                </>
              );
            })()}
          {/* Intake-classification fields: query type / source /
                medium / source URL are admin-curated. 2026-06-25
                (ops): non-admins NO LONGER see them at all - the
                fields were previously visible-but-locked on edit
                for non-admins, which added visual noise without
                giving them anything to do. Admin still sees the
                full set (editable on create + edit per the existing
                rules). The form state still round-trips the values
                on PATCH so the lead's provenance isn't clobbered
                by a non-admin edit. */}
          {isAdmin && (
            <Controlled
              name="queryTypeId"
              label="Query type"
              placeholder={queryTypes.isLoading ? 'Loading…' : 'Select type (optional)'}
              options={queryTypes.options}
              form={form}
              disabled={pending || queryTypes.isLoading}
              nullable
            />
          )}
          {isAdmin && (
            <Controlled
              name="querySourceId"
              label="Source"
              placeholder={querySources.isLoading ? 'Loading…' : 'Select source (optional)'}
              options={querySources.options}
              form={form}
              // Editable on create for admin, locked on edit for
              // admin too - source is captured once at lead creation
              // and never reassigned (server-side schema also omits
              // it from UpdateLeadRequestSchema).
              disabled={pending || querySources.isLoading || isEdit}
              hint={isEdit ? 'Locked after creation' : undefined}
              nullable
            />
          )}
          {isAdmin && (
            <Controlled
              name="queryMediumId"
              label="Medium"
              placeholder={queryMediums.isLoading ? 'Loading…' : 'Select medium (optional)'}
              options={queryMediums.options}
              form={form}
              disabled={pending || queryMediums.isLoading}
              nullable
            />
          )}
          {/* Query date is hidden from the UI - it stamps the
                moment-of-record on create (defaulted via `todayIso`)
                and is read-only for the life of the lead. The value
                still ships with the payload via form state. */}
          {/* Query source URL: admin-only too (2026-06-25). */}
          {isAdmin && (
            <UzInput
              {...form.register('querySourceUrl')}
              label="Query source URL"
              placeholder="https://instagram.com/p/..."
              disabled={pending}
              error={form.formState.errors.querySourceUrl?.message?.toString()}
              hint={
                isEdit && !isAdmin
                  ? 'Locked - admin only'
                  : 'Landing-page link the lead arrived from (Instagram / ads).'
              }
            />
          )}
          <Controlled
            name="priority"
            label="Lead Intent"
            placeholder="Select intent"
            options={PRIORITY_OPTIONS as ReadonlyArray<{ value: string; label: string }>}
            form={form}
            disabled={pending}
            required
          />
          {/* 2026-08-10. Suggested Lead Intent hint. When the operator has
              not picked an intent yet AND the travel-intent signals score
              to HIGH / NORMAL, offer the scored value as a single-click
              fill. The seller can override any time; the suggestion never
              overwrites a manual choice. Same rubric as the API's
              scoreLeadIntent (source of truth in @utazzo/shared). */}
          {(() => {
            const currentPriority = form.watch('priority');
            if (currentPriority) return null;
            const suggested = scoreLeadIntent({
              hasValidPassport: form.watch('hasValidPassport'),
              isFlightBooked: form.watch('isFlightBooked'),
              bookingTimeline: form.watch('bookingTimeline'),
              preferredTravelMode: form.watch('preferredTravelMode'),
              budgetAmount:
                typeof form.watch('budgetAmount') === 'number'
                  ? (form.watch('budgetAmount') as number)
                  : null,
            });
            if (!suggested) return null;
            return (
              <div className="-mt-2 flex items-center gap-2 text-xs text-zinc-500">
                <span>
                  Suggested: <span className="font-medium text-zinc-700">{suggested}</span>
                </span>
                <button
                  type="button"
                  className="text-primary hover:underline"
                  disabled={pending}
                  onClick={() =>
                    form.setValue('priority', suggested, {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }
                >
                  Use suggestion
                </button>
                <span className="text-zinc-400">
                  based on passport, timeline, flights, mode, budget
                </span>
              </div>
            );
          })()}
          {/* `initialStatusId` is hidden on create: the server defaults
                every new lead to `not_qualified` (ADR-0038). Stage changes
                go through the transition flow on the lead detail page. In
                edit mode the field would also be hidden because the schema
                doesn't include it; we kept the create-only conditional
                here for historical clarity but the body is empty. */}
          {/* Owner / Assigned agent.
                  - Create mode: visible to ADMIN + SALES_LEAD (hierarchy-
                    scoped options); SALES_AGENT auto-owns the lead.
                  - Edit mode:
                    * ADMIN + SALES_LEAD: editable on OPEN-bucket leads.
                      SALES_LEAD options = self + direct reports only.
                    * SALES_AGENT: read-only (picker disabled).
                    * Closed (WON / LOST) leads: disabled for everyone.
                    */}
          {(canPickAssignee || isEdit) && (
            <AgentSelect
              form={form}
              agents={assigneePickerAgents}
              disabled={pending || (isEdit && (!canReassignOwnerOnEdit || !isOpenLead))}
              requireAssignment={isSalesLead}
              unassignedHint={
                !isEdit && canLeaveUnassigned
                  ? 'Leave blank to auto-assign: the lead goes through the Lead Parsing round-robin to the next expert for its destination.'
                  : null
              }
              lockReason={
                !isEdit
                  ? null
                  : !canReassignOwnerOnEdit
                    ? "Only admins and sales leads can change a lead's owner."
                    : !isOpenLead
                      ? 'This lead is closed - reopen it before reassigning.'
                      : isSalesLead
                        ? 'You can assign this lead to yourself or your direct reports.'
                        : null
              }
            />
          )}
          {/* 2026-06-24: Branch / Created by / Assigned on were
                hidden from the form per ops request - all three are
                auto-filled at save time and don't need an operator
                surface:
                  - branchId: seeded by AgentSelect onValueChange
                    (admin / sales lead path) and by the
                    `branchByAgentId` useEffect above (sales agent
                    self-assign path). Persists with the create.
                  - createdById: seeded to `me.id` by the
                    create-mode useEffect above. The Detail page
                    still shows attribution.
                  - assignedOn: derived server-side from createdAt;
                    the form never sent anything for it. The Detail
                    page shows it.
                The form state for branchId / createdById is still
                tracked + POSTed; only the UI surface is removed. */}
        </div>
      </UzCard>

      <UzCard title="Travel">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Controlled
            name="leadCategory"
            label="Lead category"
            placeholder="Select category"
            options={LEAD_CATEGORY_OPTIONS}
            form={form}
            disabled={pending}
            required
          />
          {/* Import-captured customer origin (DB `lead_departure_city`).
                Frozen snapshot — never editable. Empty on manually
                created leads. Rendered as a plain read-only tile (not a
                disabled search picker) so it doesn't look interactive. */}
          <div className="space-y-1">
            <label className="text-xs font-medium tracking-wide text-zinc-500 uppercase">
              Customer origin city
            </label>
            <div className="flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 p-2">
              <MapPinIcon className="h-4 w-4 shrink-0 text-zinc-400" />
              <span className="flex-1 truncate text-sm text-zinc-700">
                {leadQuery.data?.departureCityText ??
                  leadQuery.data?.leadDepartureCity?.name ??
                  '—'}
              </span>
            </div>
            <p className="text-xs text-zinc-500">From the import sheet. Read-only.</p>
          </div>
          {/* Departure city: server-side search, narrowed by the
                lead category. DOMESTIC = Indian cities only;
                INTERNATIONAL = non-Indian only. The picker shows the
                currently-selected city as a compact tile with a
                "Change" button - same shape as CustomerPicker. */}
          {(() => {
            const watchedDepartureId = form.watch('departureCityId') as string | null | undefined;
            return (
              <DepartureCityPicker
                value={watchedDepartureId ?? null}
                valueLabel={departureCityLabel}
                disabled={pending}
                // Optional on create/edit, mandatory for the Active
                // transition (active-readiness) - show the asterisk
                // when this form is embedded in the "Complete the
                // lead before moving to Active" modal.
                required={focusOnActiveReadiness}
                error={form.formState.errors.departureCityId?.message?.toString()}
                onChange={({ id, label }) => {
                  form.setValue('departureCityId', id, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  setDepartureCityLabel(label);
                }}
              />
            );
          })()}
          {/* Destination. 2026-06-24: this field used to be a
                City picker (cities master, server-side search). It
                now points to the Destinations master - same list as
                "Marketing Destination" below, different label.
                Persists to `destinationCityId` for back-compat with
                the column name; the column's FK target was retargeted
                from cities to destinations in the same change.

                On edit the field stays LOCKED only when the loaded
                lead already HAS a destination - same envelope rule as
                before. Anchored on `leadQuery.data` so the lock
                state doesn't flip mid-edit. */}
          {/* 2026-06-25 (ops): Destination is now editable in edit
              mode too - operators have been asking for a way to fix
              a mis-picked destination without admin involvement.
              Marketing Destination stays locked (intake decision,
              ADR-0054 rationale). SALES_AGENT destination edits on
              an existing lead route through team-lead approval. */}
          {pendingDestinationApproval ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" aria-hidden />
                <div>
                  <div className="font-medium">
                    Destination change pending review
                    {pendingDestinationApproval.toDestination
                      ? `: ${pendingDestinationApproval.fromDestination?.name ?? 'None'} → ${pendingDestinationApproval.toDestination.name}`
                      : ''}
                  </div>
                  <div className="text-xs text-amber-800">
                    Requested by {pendingDestinationApproval.requestedBy.fullName}. The destination
                    stays unchanged until your team lead approves.
                  </div>
                  {canReviewLeadApprovalRequest(pendingDestinationApproval, me) && (
                    <Link
                      to="/leads/approvals"
                      className="mt-1 inline-block text-xs font-medium underline underline-offset-2"
                    >
                      Open pending approvals inbox
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ) : null}
          {/* 2026-07-11. Destination is now a searchable UzCombobox
              (was UzSelect via Controlled). The master can be hundreds
              of rows so a plain dropdown was hard to scan; the built-in
              search input filters as you type. `containerClassName`
              gives the popover a stable `min-w-0` parent so the
              trigger's `w-full` clips inside the grid column, and the
              long-option truncation on Command.Item labels prevents
              the list container from being pushed off-screen by an
              unusually wide entry. */}
          <div className="min-w-0">
            <UzCombobox
              name="destinationCityId"
              label="Destination"
              required
              placeholder={
                destinations.isLoading
                  ? 'Loading…'
                  : destinations.options.length === 0
                    ? 'No destinations available'
                    : 'Pick a destination'
              }
              searchPlaceholder="Search destinations…"
              options={destinations.options.map((o) => ({ value: o.value, label: o.label }))}
              value={(form.watch('destinationCityId') as string | undefined) || undefined}
              onValueChange={(v) => {
                form.setValue('destinationCityId', v || '', { shouldValidate: true });
              }}
              // 2026-07-09. Sales agents can no longer edit the destination
              // inline - they trigger a change via the "Change destination"
              // button below, which fires an approval request. Admins and
              // sales leads keep inline edit.
              disabled={
                pending ||
                pendingDestinationApproval !== null ||
                destinations.isLoading ||
                destinations.options.length === 0 ||
                (isEdit && isAgentOrLead)
              }
              error={
                (form.formState.errors as Record<string, { message?: string } | undefined>)[
                  'destinationCityId'
                ]?.message
              }
              hint={
                isEdit && isAgentOrLead
                  ? 'Locked. Click "Change destination" below to request a change from your team lead.'
                  : 'Required. Curated by the marketing team in Masters > Destinations.'
              }
            />
          </div>
          {/* 2026-07-09. Manual destination-change trigger, inline
              beneath the Destination field so the action sits right
              next to what it acts on. Sales agents only, on edit,
              with no pending destination request already in flight
              (the banner above surfaces that state). Opens the
              approval dialog; the field itself stays locked until a
              team lead approves the request. */}
          {isEdit && isAgentOrLead && !pendingDestinationApproval && leadQuery.data && (
            <div className="-mt-2 flex flex-wrap items-center gap-3 sm:col-span-2">
              <UzButton
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowChangeDestinationDialog(true)}
                disabled={pending || destinations.isLoading || destinations.options.length === 0}
              >
                <MapPinIcon className="mr-1.5 h-4 w-4" aria-hidden />
                Change destination
              </UzButton>
              <span className="text-xs text-zinc-500">
                Sends a request to your team lead for approval. The destination unlocks once
                approved.
              </span>
            </div>
          )}
          {/* Marketing Destination. Same destinations master as
                "Destination" above, just a different label. Optional
                on create, DISABLED entirely on edit per the 2026-06-24
                form refactor - the marketing tag is a create-time
                decision and isn't meant to drift after the lead is
                in motion. */}
          <ControlledCombobox
            name="destinationId"
            label="Marketing Destination"
            placeholder={
              destinations.isLoading
                ? 'Loading…'
                : destinations.options.length === 0
                  ? 'No destinations available'
                  : 'Pick a marketing destination'
            }
            options={destinations.options}
            form={form}
            disabled={
              pending || isEdit || destinations.isLoading || destinations.options.length === 0
            }
            nullable
            hint={
              isEdit
                ? 'Marketing tag is locked after create. Reach out to admin to change.'
                : 'Optional. Curated by the marketing team in Masters > Destinations.'
            }
          />
          {/* 2026-06-27. Date constraints:
                - Travel start: never before today (no back-date).
                - Travel end:   strictly after Travel start - never the same
                                day (or today if start is empty). */}
          {(() => {
            const today = startOfDay(new Date());
            const startRaw = form.watch('expectedTravelStart') as string | null | undefined;
            const startDate = startRaw ? new Date(startRaw) : null;
            const hasStart = startDate !== null && !Number.isNaN(startDate.getTime());
            // Travel end must be strictly AFTER travel start (never the same
            // day), so the earliest selectable end is the day after start.
            const endMin = (() => {
              if (!hasStart) return today;
              const d = startOfDay(startDate);
              d.setDate(d.getDate() + 1);
              return d;
            })();
            return (
              <>
                <DateControlled
                  name="expectedTravelStart"
                  label="Travel start"
                  form={form}
                  disabled={pending}
                  required
                  minDate={today}
                  hint="Cannot be a past date."
                />
                <DateControlled
                  name="expectedTravelEnd"
                  label="Travel end"
                  form={form}
                  disabled={pending}
                  required
                  minDate={endMin}
                  hint={hasStart ? 'Must be after travel start.' : 'Cannot be a past date.'}
                />
              </>
            );
          })()}
          {/* Budget currency picker is hidden. Every lead is captured
                in INR (defaulted by the useEffect above). The field
                stays in form state so the existing value round-trips
                on edit, and admins can still re-currency a lead via a
                future settings screen if needed. */}
          <NumberControlled
            name="budgetAmount"
            label="Total Budget for destination"
            hint={
              isEdit
                ? 'Total ballpark the customer mentioned.'
                : 'Optional at intake. Required before moving the lead to Active.'
            }
            form={form}
            disabled={pending}
            min={0}
            precision={2}
          />
          <NumberControlled
            name="travellerAdults"
            label="Adults (12+)"
            form={form}
            disabled={pending}
            min={0}
            precision={0}
            required
          />
          <NumberControlled
            name="travellerChildren"
            label="Children (2-12)"
            form={form}
            disabled={pending}
            min={0}
            precision={0}
            required
          />
          <NumberControlled
            name="travellerInfants"
            label="Infants (0-2)"
            form={form}
            disabled={pending}
            min={0}
            precision={0}
            required
          />
        </div>

        {/* 2026-06-25: per-child ages. Dynamically renders one
            integer input per child based on the `Children` count
            above; clamped 0-120; persists as an int[] on the lead
            row. Editable for ALL roles (no admin lock). Skipped
            entirely when Children = 0 so the section stays clean. */}
        <ChildAgesRow form={form} disabled={pending} />

        {/* ADR-0035 legacy parity - additional travel intent fields. Kept
              in a separate block + divider so the operator scanning the
              core fields above isn't visually overwhelmed.
              2026-07-09. Hidden in the "Complete the lead before moving
              to Active" modal (focusOnActiveReadiness=true) - none of
              these fields participate in the readiness gate, so
              surfacing them adds noise to what should be a targeted
              form. */}
        {(!focusOnActiveReadiness || focusIncludeTravelIntent) && (
          <div className="mt-6 border-t border-zinc-100 pt-4">
            <h3 className="mb-3 text-sm font-semibold text-zinc-800">Additional travel details</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {/* 2026-06-27. TTV is always read-only on this form -
                  the value is filled in automatically when an
                  itinerary is sent to the client (the
                  itinerary-send flow stamps the package total onto
                  the lead). The field shows the current snapshot
                  for context but ops can't hand-edit it here. */}
              <NumberControlled
                name="ttvAmount"
                label="TTV of package sent to client"
                form={form}
                min={0}
                precision={2}
                hint="Pre-tax selling value (before GST/TCS). Auto-filled when an itinerary is sent. Not editable here."
              />
              <Controlled
                name="preferredHotelCategory"
                label="Preferred hotel category"
                placeholder="Any"
                options={HOTEL_CATEGORY_OPTIONS}
                form={form}
                disabled={pending}
                nullable
                asNumber
              />
              <Controlled
                name="bookingTimeline"
                label="How soon will customer book?"
                placeholder="Select"
                options={BOOKING_TIMELINE_OPTIONS}
                form={form}
                disabled={pending}
                nullable
              />
              <ThreeStateBoolean
                name="isFirstTimeTraveler"
                label="First time traveler?"
                form={form}
                disabled={pending}
              />
              <ThreeStateBoolean
                name="isHoneymoonCouple"
                label="Honeymoon couple?"
                form={form}
                disabled={pending}
              />
              <ThreeStateBoolean
                name="hasValidPassport"
                label="Customer has valid passport?"
                form={form}
                disabled={pending}
              />
              <ThreeStateBoolean
                name="isFlightBooked"
                label="Flight already booked?"
                form={form}
                disabled={pending}
              />
              <UzInput
                {...form.register('preferredTravelMode')}
                label="Preferred travel mode"
                placeholder="e.g. premium, economy"
                disabled={pending}
                error={form.formState.errors.preferredTravelMode?.message?.toString()}
              />
              <ThreeStateBoolean
                name="isDecisionMaker"
                label="Customer is decision maker?"
                form={form}
                disabled={pending}
              />
              <Controlled
                name="mealPlanId"
                label="Meal plan"
                placeholder={mealPlans.isLoading ? 'Loading...' : 'Select'}
                options={mealPlans.options}
                form={form}
                disabled={pending || mealPlans.isLoading}
                nullable
              />
              {/* 2026-07-09 (revised). Expected payment date is only
                meaningful once the lead is warm-to-book, so we render
                the field ONLY when the lead is currently sitting on
                Active + Hot (edit mode). On create the field is hidden
                entirely - a brand-new lead can't be Active / Hot yet,
                so surfacing it would just add noise to the intake
                form. When the field is shown, the required asterisk +
                hint make the expectation clear; the schema stays
                `.nullable().optional()` so a save with an empty value
                doesn't 400 - validation stays soft. */}
              {isActiveHot && (
                <DateControlled
                  name="expectedPaymentDate"
                  label="Expected payment date"
                  form={form}
                  disabled={pending}
                  required
                  hint="Track the expected payment date - this lead is Active / Hot."
                />
              )}
              <UzInput
                {...form.register('requirementSummary')}
                label="Requirement"
                placeholder="e.g. Bali | 7th Aug | 4 Adults | 6 N 7 D | Delhi"
                disabled={pending}
                error={form.formState.errors.requirementSummary?.message?.toString()}
                containerClassName="sm:col-span-3"
                hint="Single-line structured trip summary."
              />
            </div>
          </div>
        )}
      </UzCard>

      {/* 2026-07-09. Notes card hidden in the "Complete the lead
          before moving to Active" modal - it isn't part of the
          readiness gate, so surfacing it in that focused modal is
          just noise. Everywhere else it renders as usual. */}
      {!focusOnActiveReadiness && (
        <UzCard title="Notes">
          <UzTextarea
            {...form.register('notes')}
            label="What the customer told us"
            placeholder="Preferences, must-haves, constraints, free-text context."
            rows={4}
            disabled={pending}
          />
        </UzCard>
      )}

      <div className="flex justify-end gap-2 pb-8">
        <UzButton
          variant="ghost"
          onClick={() => navigate(isEdit && id ? `/leads/${id}` : '/leads')}
          disabled={pending}
        >
          Cancel
        </UzButton>
        <UzButton onClick={onSubmit} loading={pending}>
          <Check className="mr-1.5 h-4 w-4" />
          {isEdit ? 'Save changes' : 'Create lead'}
        </UzButton>
      </div>

      {/* 2026-07-09. Manual destination-change dialog for sales agents.
          Fires POST /leads/:id/status-change-requests with kind
          `DESTINATION_CHANGE`; the server routes it to the requester's
          reporting manager. Success closes + toasts; failure surfaces
          the server error via `extractServerError` (see use-leads). */}
      <UzDialog
        open={showChangeDestinationDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowChangeDestinationDialog(false);
            setChangeDestinationId('');
            setChangeDestinationNote('');
          }
        }}
        title="Request destination change"
        description="Pick the new destination. Your request goes to your team lead for approval; the current destination stays until they approve."
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <UzButton
              variant="ghost"
              onClick={() => {
                setShowChangeDestinationDialog(false);
                setChangeDestinationId('');
                setChangeDestinationNote('');
              }}
              disabled={requestApproval.isPending}
            >
              Cancel
            </UzButton>
            <UzButton
              onClick={async () => {
                if (!id || !changeDestinationId) return;
                if (changeDestinationId === originalDestinationCityId) {
                  return;
                }
                try {
                  await requestApproval.mutateAsync({
                    id,
                    payload: {
                      kind: 'DESTINATION_CHANGE',
                      toDestinationId: changeDestinationId,
                      requestorNote: changeDestinationNote.trim() || null,
                    },
                  });
                  setShowChangeDestinationDialog(false);
                  setChangeDestinationId('');
                  setChangeDestinationNote('');
                } catch {
                  // toast + focus already fired by the mutation's onError.
                }
              }}
              loading={requestApproval.isPending}
              disabled={!changeDestinationId || changeDestinationId === originalDestinationCityId}
            >
              Send for approval
            </UzButton>
          </div>
        }
      >
        <div className="space-y-4">
          <UzSelect
            name="changeDestinationId"
            label="New destination"
            placeholder={destinations.isLoading ? 'Loading…' : 'Pick the destination to request'}
            options={destinations.options.filter((opt) => opt.value !== originalDestinationCityId)}
            value={changeDestinationId || undefined}
            onValueChange={(v) => setChangeDestinationId(v ?? '')}
            required
            disabled={requestApproval.isPending || destinations.isLoading}
          />
          <UzTextarea
            name="changeDestinationNote"
            label="Note for your team lead (optional)"
            rows={3}
            value={changeDestinationNote}
            onChange={(e) => setChangeDestinationNote(e.target.value)}
            disabled={requestApproval.isPending}
            placeholder="e.g. customer switched from Bali to Thailand"
          />
        </div>
      </UzDialog>
    </div>
  );

  if (embedded) return formBody;

  return (
    <PageShell
      title={isEdit ? `Edit lead - ${leadQuery.data?.customer.displayName ?? ''}` : 'New lead'}
      description={
        isEdit
          ? 'Update the enquiry. Status changes go through the lead detail page.'
          : 'Capture an enquiry. Once saved, the lead opens in the inbox.'
      }
      actions={
        <div className="flex gap-2">
          <UzButton
            variant="ghost"
            size="sm"
            onClick={() => navigate(isEdit && id ? `/leads/${id}` : '/leads')}
          >
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Cancel
          </UzButton>
          <UzButton onClick={onSubmit} loading={pending}>
            <Check className="mr-1.5 h-4 w-4" />
            {isEdit ? 'Save changes' : 'Create lead'}
          </UzButton>
        </div>
      }
    >
      {formBody}
    </PageShell>
  );
};

// ----- Helpers (form-control bridges) -----
//
// The two RHF form instances (create vs update) have different generics; the
// helpers below take an untyped form ref because zod-resolver enforces the
// shape at submit anyway. Keeping it loose here avoids declaring two parallel
// versions of every control.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyForm = ReturnType<typeof useForm<any>>;

interface ControlledProps {
  name: string;
  label: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  form: AnyForm;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  /** When true, the empty string select option means `null` on the form. */
  nullable?: boolean;
  hint?: string;
  /**
   * Coerce the picked option value to a number on emit (string → number).
   * Used for selects whose underlying schema field is numeric - e.g.
   * `preferredHotelCategory` (1..5) where the dropdown values are stringly
   * typed but the form / API expect Int.
   */
  asNumber?: boolean;
}

const Controlled = ({
  name,
  label,
  options,
  form,
  placeholder,
  disabled,
  required,
  nullable,
  hint,
  asNumber,
}: ControlledProps): React.JSX.Element => {
  const raw = form.watch(name);
  // Numeric form values need to be stringified for the UzSelect's `value`
  // prop, which is always a string. We round-trip back to number on emit.
  const value = raw === null || raw === undefined ? '' : String(raw);
  const error = (form.formState.errors as Record<string, { message?: string } | undefined>)[name]
    ?.message;
  return (
    <UzSelect
      name={name}
      label={label}
      hint={hint}
      placeholder={placeholder}
      options={options}
      value={value || undefined}
      onValueChange={(v) => {
        const next =
          v === null || v === undefined || v === ''
            ? nullable
              ? null
              : ''
            : asNumber
              ? Number(v)
              : v;
        form.setValue(name, next, { shouldValidate: true });
      }}
      required={required}
      disabled={disabled}
      error={error}
    />
  );
};

/**
 * Searchable variant of `Controlled` for long option lists (e.g. the
 * Destination / Marketing Destination masters, which run to hundreds of
 * cities). Renders `UzCombobox` instead of `UzSelect` so the operator gets a
 * type-to-filter search box, and the popover is width-clamped to the trigger
 * (`w-(--radix-popover-trigger-width)`) so the list no longer overflows past
 * the form. Same value/emit contract as `Controlled` (string ids only - no
 * `asNumber`, these are always UUIDs).
 */
const ControlledCombobox = ({
  name,
  label,
  options,
  form,
  placeholder,
  disabled,
  required,
  nullable,
  hint,
}: ControlledProps): React.JSX.Element => {
  const raw = form.watch(name);
  const value = raw === null || raw === undefined ? '' : String(raw);
  const error = (form.formState.errors as Record<string, { message?: string } | undefined>)[name]
    ?.message;
  return (
    <UzCombobox
      name={name}
      label={label}
      hint={hint}
      placeholder={placeholder}
      searchPlaceholder="Search destinations…"
      options={options}
      value={value || undefined}
      onValueChange={(v) => {
        const next = v === null || v === undefined || v === '' ? (nullable ? null : '') : v;
        form.setValue(name, next, { shouldValidate: true });
      }}
      required={required}
      disabled={disabled}
      error={error}
    />
  );
};

/**
 * Three-state Yes / No / Unknown picker - emits `true`, `false`, or `null`.
 * Used for the ADR-0035 boolean parity fields (first-time traveler,
 * honeymoon, valid passport, decision maker) where "operator hasn't asked
 * yet" is a real third state, distinct from a definitive No.
 */
interface ThreeStateBooleanProps {
  name: string;
  label: string;
  form: AnyForm;
  disabled?: boolean;
  hint?: string;
}

const ThreeStateBoolean = ({
  name,
  label,
  form,
  disabled,
  hint,
}: ThreeStateBooleanProps): React.JSX.Element => {
  const raw = form.watch(name) as boolean | null | undefined;
  const stringValue = raw === true ? 'true' : raw === false ? 'false' : '';
  const error = (form.formState.errors as Record<string, { message?: string } | undefined>)[name]
    ?.message;
  return (
    <UzSelect
      name={name}
      label={label}
      hint={hint}
      placeholder="Not specified"
      options={THREE_STATE_OPTIONS}
      value={stringValue || undefined}
      onValueChange={(v) => {
        const next = v === 'true' ? true : v === 'false' ? false : null;
        form.setValue(name, next, { shouldValidate: true });
      }}
      disabled={disabled}
      error={error}
    />
  );
};

interface NumberControlledProps {
  name: string;
  label: string;
  form: AnyForm;
  disabled?: boolean;
  hint?: string;
  min?: number;
  precision?: number;
  /// 2026-06-25: pass the asterisk down to the underlying input. The
  /// zod schema still owns hard enforcement; this is the visual.
  required?: boolean;
}

/**
 * Custom select for "Assigned to" - picks the lead's owner AND auto-fills
 * `branchId` from the agent's branch in the same `setValue` cycle. Mirrors
 * the legacy "select agent → branch auto-populates" behaviour. The operator
 * can still override the branch manually after the agent is picked.
 *
 * In edit mode the form is hydrated once via `form.reset()` which doesn't
 * fire this onValueChange - so an edit hydration that loads
 * `(assignedAgent, branch)` from the lead won't accidentally overwrite the
 * lead's branch with the agent's current branch.
 */
interface AgentSelectProps {
  form: AnyForm;
  agents: {
    options: ReadonlyArray<{ value: string; label: string }>;
    branchByAgentId: Map<string, string | null>;
    isLoading: boolean;
  };
  disabled?: boolean;
  /// When set, shown in place of the default branch-autofill hint
  /// to explain WHY the picker is locked. Used for the 2026-06-23
  /// reassignment-policy lock (non-admin user or closed lead).
  lockReason?: string | null;
  /// When true, clearing the picker is ignored (SALES_LEAD cannot leave
  /// a lead unowned on create / edit).
  requireAssignment?: boolean;
  /// Create mode, when blank is a real choice: what happens to an unowned
  /// lead (Lead Parsing round-robin). Shown as the placeholder and the hint.
  unassignedHint?: string | null;
}

const AgentSelect = ({
  form,
  agents,
  disabled,
  lockReason,
  requireAssignment = false,
  unassignedHint = null,
}: AgentSelectProps): React.JSX.Element => {
  const value = (form.watch('assignedAgentId') ?? '') as string;
  const error = (form.formState.errors as Record<string, { message?: string } | undefined>)[
    'assignedAgentId'
  ]?.message;
  return (
    <UzSelect
      name="assignedAgentId"
      label="Owner (assigned agent)"
      placeholder={
        agents.isLoading
          ? 'Loading…'
          : requireAssignment
            ? 'Select agent'
            : unassignedHint
              ? 'Auto-assign (round-robin)'
              : 'Unassigned'
      }
      options={agents.options}
      value={value || undefined}
      onValueChange={(v) => {
        if (requireAssignment && (!v || v.length === 0)) return;
        const nextAgentId = v && v.length > 0 ? v : null;
        form.setValue('assignedAgentId', nextAgentId, { shouldValidate: true });
        // Auto-fill / clear branch in lockstep with the agent change. We only
        // overwrite branchId when there's an agent (so clearing the agent
        // leaves the branch as the operator left it) - pragmatic default.
        if (nextAgentId) {
          const branchId = agents.branchByAgentId.get(nextAgentId);
          if (branchId !== undefined) {
            form.setValue('branchId', branchId, { shouldValidate: true });
          }
        }
      }}
      disabled={disabled || agents.isLoading}
      error={error}
      hint={
        lockReason ??
        unassignedHint ??
        (requireAssignment
          ? "Pick yourself or a direct report. Branch fills in from the agent's home branch."
          : "Branch fills in from the agent's home branch - change it below if needed.")
      }
    />
  );
};

/**
 * Date-only controlled select backed by `UzDatePicker`. The form state stores
 * the date as a `YYYY-MM-DD` string (what `z.iso.date()` expects); we coerce
 * to a `Date` for the picker and back on emit. Avoids native `<input
 * type="date">` whose UX varies wildly across browsers (legacy parity per
 * ADR-0032 §13).
 */
interface DateControlledProps {
  name: string;
  label: string;
  form: AnyForm;
  disabled?: boolean;
  hint?: string;
  /// 2026-06-25: surface the asterisk on mandatory date fields.
  /// Passes straight through to the underlying picker; actual
  /// enforcement still lives in the zod schema.
  required?: boolean;
  /// 2026-06-27. Earliest date the operator can pick. Anything
  /// strictly before this renders as a disabled day in the
  /// calendar AND a value type-in is rejected by `onValueChange`.
  /// Format: a Date (compared by calendar day, time stripped).
  minDate?: Date | null;
}

/** Strip the time-of-day for day-only comparisons. */
function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

const DateControlled = ({
  name,
  label,
  form,
  disabled,
  hint,
  required,
  minDate,
}: DateControlledProps): React.JSX.Element => {
  const raw = form.watch(name) as string | null | undefined;
  const date = raw ? new Date(raw) : null;
  const error = (form.formState.errors as Record<string, { message?: string } | undefined>)[name]
    ?.message;
  // 2026-06-27. Compose a per-day disable predicate from `minDate`
  // (and the existing boolean `disabled` for the whole control).
  // The underlying picker accepts `disabled: (date) => boolean` for
  // calendar-cell-level disabling; we use it here so back-dated
  // days literally can't be clicked.
  const minDay = minDate ? startOfDay(minDate) : null;
  const disabledForCalendar: boolean | ((day: Date) => boolean) = disabled
    ? true
    : minDay
      ? (day: Date) => startOfDay(day).getTime() < minDay.getTime()
      : false;
  return (
    <UzModernDatePicker
      label={label}
      hint={hint}
      required={required}
      value={Number.isNaN(date?.getTime() ?? NaN) ? null : (date as Date | null)}
      onValueChange={(d: Date | null) => {
        // Belt-and-braces: even if a malformed value sneaks in (paste
        // / programmatic set), clamp it to null when it would violate
        // the minDate floor.
        if (d && minDay && startOfDay(d).getTime() < minDay.getTime()) {
          form.setValue(name, null, { shouldValidate: true });
          return;
        }
        const iso = d ? toIsoDate(d) : null;
        form.setValue(name, iso, { shouldValidate: true });
      }}
      disabled={disabledForCalendar}
      error={error}
    />
  );
};

/** YYYY-MM-DD in the local timezone so the user's clock matches the input. */
function toIsoDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const NumberControlled = ({
  name,
  label,
  form,
  disabled,
  hint,
  min,
  precision = 0,
  required,
}: NumberControlledProps): React.JSX.Element => {
  // Register so RHF tracks the field even though we drive it via setValue
  // (UzNumberInput is not a native RHF `register` input). Without this,
  // typed values can render in the draft UI yet never join the submit
  // payload - which is exactly how Adults/Children reset to 1/0 on create.
  // Do NOT unregister on cleanup: remounts (Strict Mode / conditional
  // sections) would wipe the committed value mid-edit.
  useEffect(() => {
    form.register(name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  const raw = form.watch(name) as number | null | undefined;
  const error = (form.formState.errors as Record<string, { message?: string } | undefined>)[name]
    ?.message;
  return (
    <UzNumberInput
      name={name}
      label={label}
      hint={hint}
      min={min}
      precision={precision}
      required={required}
      value={raw === null || raw === undefined ? '' : raw}
      // Never write `null` into RHF: zod rejects null on number fields, and
      // a mid-edit clear used to poison state so submit fell through to the
      // old schema `.default(1)` / form defaultValues (Adults→1, Children→0).
      // Empty draft stays visual-only; blur restores the last committed number.
      onValueChange={(v) => {
        if (v === null) return;
        form.setValue(name, v, {
          shouldDirty: true,
          shouldValidate: true,
          shouldTouch: true,
        });
      }}
      error={error}
      disabled={disabled}
    />
  );
};

/**
 * 2026-06-25: per-child age inputs rendered for ALL roles.
 *
 * Renders one integer input per child indicated by `travellerChildren`
 * above. The form's `childAges` int[] is kept in sync element-by-element:
 *   - travellerChildren grows -> trailing slots show empty inputs the
 *     operator can fill (or leave blank for an unknown age)
 *   - travellerChildren shrinks -> trailing entries are silently dropped
 *     on the next render (the array is sliced to the visible count
 *     inside the render, so a 4→2 transition doesn't persist 4 ages)
 *
 * Each input clamps to [0, 120]. The persisted array length may differ
 * from `travellerChildren` while the form is being filled - the server's
 * schema treats them as independent inputs (zod `array(number().min(0).max(120))`).
 */
const ChildAgesRow = ({
  form,
  disabled,
}: {
  form: AnyForm;
  disabled?: boolean;
}): React.JSX.Element | null => {
  const childrenCount = (form.watch('travellerChildren') as number | null | undefined) ?? 0;
  const childAges = (form.watch('childAges') as number[] | null | undefined) ?? [];
  if (childrenCount <= 0) return null;
  // Snap the visible array to the current child count - extra slots
  // get an empty value, surplus ages drop off (the persisted array
  // never holds more ages than there are children).
  const slots = Array.from({ length: childrenCount }, (_, i) =>
    typeof childAges[i] === 'number' ? childAges[i] : null,
  );
  const updateAt = (idx: number, next: number | null): void => {
    // Build a fresh array trimmed to the current count + with the
    // edit applied at the target index.
    const trimmed = Array.from({ length: childrenCount }, (_, i) =>
      i === idx ? next : (slots[i] as number | null),
    );
    // Persist only the defined entries - undefined/null gaps stay as
    // gaps in the form state and are stripped on save so they don't
    // ship a sparse array to the server.
    form.setValue(
      'childAges',
      trimmed.filter((v): v is number => typeof v === 'number'),
      { shouldDirty: true },
    );
  };
  return (
    <div className="mt-3 rounded-md border border-zinc-100 bg-zinc-50/50 p-3">
      <div className="mb-2 text-sm font-medium text-zinc-700">
        Child ages
        <span className="ml-2 text-xs font-normal text-zinc-500">
          One per child. Leave blank if not known yet.
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {slots.map((v, i) => (
          <UzNumberInput
            key={i}
            name={`childAge-${i}`}
            label={`Child ${i + 1}`}
            min={0}
            precision={0}
            value={v === null ? '' : v}
            onValueChange={(next) =>
              updateAt(
                i,
                typeof next === 'number' && Number.isFinite(next)
                  ? Math.min(120, Math.max(0, next))
                  : null,
              )
            }
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Map a LeadDetail's nested customer ref back to the CustomerListItem shape
 * the picker expects. Only the fields the picker reads are populated.
 */
function toListItem(lead: LeadDetail): CustomerListItem {
  return {
    id: lead.customer.id,
    kind: lead.customer.kind,
    displayName: lead.customer.displayName,
    email: lead.customer.email,
    mobileNumber: lead.customer.mobileNumber,
  } as CustomerListItem;
}

// ----- CountryCodePicker -----
//
// Searchable picker sourced from the curated Country master. The form
// state stores just the dial code (e.g. "91") for `customer.mobileCountry`
// - the legacy column shape we have to keep. The picker uses each country's
// `isoCode` as the internal option `value` (uniquely identifies the row,
// even when two countries share the same dial code - e.g. US and Canada
// both +1) and emits the matching `phoneCode` to the parent on change.
// Default selection is India when no value is set.
interface CountryCodePickerProps {
  /** Dial code without the leading "+", e.g. "91". `null` means unset. */
  value: string;
  onChange: (dial: string) => void;
  disabled?: boolean;
}

// PAGINATION.MAX_PAGE_SIZE (shared constant) caps server-side pageSize at 200
// - exceeding it 400s the request as a zod validation error and the list
// renders empty. 200 is comfortably above the curated Country master size
// (~18 seeded rows, ~250 even if expanded to every ISO country).
const COUNTRIES_PAGE_SIZE = 200;

const CountryCodePicker = ({
  value,
  onChange,
  disabled,
}: CountryCodePickerProps): React.JSX.Element => {
  const countries = useCountriesList({
    page: 1,
    pageSize: COUNTRIES_PAGE_SIZE,
    sortBy: 'sortOrder',
    sortDir: 'asc',
    isActive: true,
  });

  const items = useMemo(() => countries.data?.data ?? [], [countries.data]);

  // Resolve the *isoCode* (UzCombobox value) from the stored dial code. We
  // prefer the country flagged "in" when the dial code is "91" (operators
  // expect India), then fall back to the first matching row. Countries
  // with a NULL `phoneCode` can never match a dial-code lookup - drop them
  // early so the comparison doesn't have to defend against null.
  const selectedIso = useMemo(() => {
    if (!value) return '';
    const target = normalizeDial(value);
    const matches = items.filter(
      (c): c is (typeof items)[number] & { phoneCode: string } =>
        Boolean(c.phoneCode) && normalizeDial(c.phoneCode!) === target,
    );
    if (matches.length === 0) return '';
    const india = matches.find((c) => c.isoCode.toLowerCase() === 'in');
    return india?.isoCode ?? matches[0]?.isoCode ?? '';
  }, [items, value]);

  const options = useMemo(
    () =>
      items
        .filter((c) => Boolean(c.phoneCode))
        .map((c) => ({
          value: c.isoCode,
          // Trigger label kept short ("🇮🇳 +91"); the dropdown row shows the
          // country name as the description so duplicates are
          // distinguishable while picking.
          label: `${c.flagEmoji ?? '🏳'} +${normalizeDial(c.phoneCode!)}`,
          description: c.name,
        })),
    [items],
  );

  // Default to India when the form has no value yet (create mode mounts
  // with `customerForm.mobileCountry = '91'` already, so this is a no-op
  // there - but defensive for any path that leaves it blank).
  useEffect(() => {
    if (value) return;
    if (items.length === 0) return;
    const india = items.find((c) => c.isoCode.toLowerCase() === 'in');
    if (india?.phoneCode) onChange(normalizeDial(india.phoneCode));
  }, [items, value, onChange]);

  return (
    <UzCombobox
      name="customer-mobileCountry"
      label="Country"
      placeholder={countries.isLoading ? 'Loading…' : 'Select'}
      searchPlaceholder="Search country…"
      options={options}
      value={selectedIso}
      onValueChange={(iso) => {
        const next = items.find((c) => c.isoCode === iso);
        if (next?.phoneCode) onChange(normalizeDial(next.phoneCode));
      }}
      disabled={disabled || countries.isLoading}
      loading={countries.isLoading}
    />
  );
};

/** Strip any "+" prefix; we store / emit the bare dial number ("91"). */
function normalizeDial(raw: string): string {
  return raw.replace(/^\+/, '').trim();
}
